import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import SimManager from '../components/SimManager';
import SpinWheel from '../components/SpinWheel';
import CustomerCreditPanel from '../components/CustomerCreditPanel';

const Admin = () => {
  const navigate = useNavigate();
  const [authenticated, setAuthenticated] = useState(false);
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [collections, setCollections] = useState([]);
  const [newsletterSubscribers, setNewsletterSubscribers] = useState([]);
  const [giveawayEntries, setGiveawayEntries] = useState([]);
  const [revenue, setRevenue] = useState({});
  const [loading, setLoading] = useState(false);
  const [showSpinWheel, setShowSpinWheel] = useState(false);
  const [recentWinners, setRecentWinners] = useState([]);
  
  // Lookbook editing state
  const [editingLookbook, setEditingLookbook] = useState(null);
  const [showLookbookForm, setShowLookbookForm] = useState(false);
  const [lookbookForm, setLookbookForm] = useState({
    name: '',
    season: '',
    description: '',
    product_ids: [],
    banner_color: '#F7F6D8',
    emoji_icons: ['📖'],
    image_url: ''
  });

  // Template email modal state
  const [templateModal, setTemplateModal] = useState(null); // { key, label, subject, html }
  const [templateSubject, setTemplateSubject] = useState('');
  const [templateTestEmail, setTemplateTestEmail] = useState('theeloop530@gmail.com');
  const [templateSending, setTemplateSending] = useState(false);
  const [blastProgress, setBlastProgress] = useState(null); // { status, total_queued, sent, failed, skipped_unsubscribed, failed_emails }

  // Custom email composer state (Newsletter tab)
  const [customSubject, setCustomSubject] = useState('');
  const [customBody, setCustomBody] = useState('');
  const [customTestEmail, setCustomTestEmail] = useState('theeloop530@gmail.com');
  const [customPreviewHtml, setCustomPreviewHtml] = useState(null); // rendered HTML string when preview is open
  const [customSending, setCustomSending] = useState(false);
  const [customAudience, setCustomAudience] = useState(null); // { total_customers, sendable, skipped_unsubscribed }

  // Shopify sync state (Products tab)
  const [shopifySyncing, setShopifySyncing] = useState(false);
  const [shopifySyncResult, setShopifySyncResult] = useState(null); // report from last sync/preview
  const [productVisibilityFilter, setProductVisibilityFilter] = useState('all'); // 'all' | 'hidden' | 'visible'

  // Player stats (Players tab)
  const [playerStats, setPlayerStats] = useState([]);
  const [playerStatsTotal, setPlayerStatsTotal] = useState(0);
  const [playerStatsLoading, setPlayerStatsLoading] = useState(false);

  // Payment reconciliation
  const [syncingPayments, setSyncingPayments] = useState(false);

  // Retry Payment modal state
  const [retryPaymentOrder, setRetryPaymentOrder] = useState(null); // the order object
  const [retryPaymentLink, setRetryPaymentLink] = useState('');
  const [retryPaymentLoading, setRetryPaymentLoading] = useState(false);
  const [retryPaymentEmailMessage, setRetryPaymentEmailMessage] = useState('');
  const [retryPaymentSendingEmail, setRetryPaymentSendingEmail] = useState(false);
  const [retryPaymentCopied, setRetryPaymentCopied] = useState(false);

  const openRetryPaymentModal = async (order) => {
    setRetryPaymentOrder(order);
    setRetryPaymentLink('');
    setRetryPaymentCopied(false);
    setRetryPaymentLoading(true);
    try {
      const res = await api.adminCreateRetryPaymentLink(order._id);
      if (res.payment_url) {
        setRetryPaymentLink(res.payment_url);
      } else {
        alert('✗ Failed to create payment link');
        setRetryPaymentOrder(null);
      }
    } catch (e) {
      alert('✗ ' + (e.message || 'Failed to create payment link'));
      setRetryPaymentOrder(null);
    } finally {
      setRetryPaymentLoading(false);
    }
  };

  const closeRetryPaymentModal = () => {
    if (retryPaymentLoading || retryPaymentSendingEmail) return;
    setRetryPaymentOrder(null);
    setRetryPaymentLink('');
    setRetryPaymentEmailMessage('');
    setRetryPaymentCopied(false);
  };

  const handleCopyRetryLink = async () => {
    try {
      await navigator.clipboard.writeText(retryPaymentLink);
      setRetryPaymentCopied(true);
      setTimeout(() => setRetryPaymentCopied(false), 2500);
    } catch (_e) {
      // Fallback
      const ta = document.createElement('textarea');
      ta.value = retryPaymentLink;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); setRetryPaymentCopied(true); setTimeout(() => setRetryPaymentCopied(false), 2500); } catch (_err) {}
      document.body.removeChild(ta);
    }
  };

  const handleEmailRetryLink = async () => {
    if (!retryPaymentOrder) return;
    const email = retryPaymentOrder.email || retryPaymentOrder.customer_email;
    if (!email) {
      alert('This order has no customer email on file. Copy the link and send it manually.');
      return;
    }
    if (!window.confirm(`Send payment link to ${email}?`)) return;
    setRetryPaymentSendingEmail(true);
    try {
      const res = await api.adminEmailRetryPaymentLink(retryPaymentOrder._id, retryPaymentEmailMessage || null);
      if (res.success) {
        alert(`✓ PAYMENT LINK EMAILED\n\nSent to: ${res.sent_to}\n\nThe customer will receive a retro-styled email with a secure "Complete Payment" button.`);
        closeRetryPaymentModal();
      } else {
        alert('✗ Failed to send email');
      }
    } catch (e) {
      alert('✗ ' + (e.message || 'Failed to send email'));
    } finally {
      setRetryPaymentSendingEmail(false);
    }
  };

  const loadPlayerStats = async () => {
    setPlayerStatsLoading(true);
    try {
      const data = await api.adminGetPlayerStats();
      setPlayerStats(Array.isArray(data.stats) ? data.stats : []);
      setPlayerStatsTotal(data.total_selections || 0);
    } catch (e) {
      console.error('Failed to load player stats:', e);
    } finally {
      setPlayerStatsLoading(false);
    }
  };

  const handleSyncPayments = async () => {
    if (!window.confirm('Reconcile all pending orders with Stripe / PayPal? Orders confirmed as paid by the provider will be marked paid locally.')) return;
    setSyncingPayments(true);
    try {
      const result = await api.adminSyncPayments();
      alert(
        `✓ SYNC COMPLETE\n\n` +
        `Checked: ${result.checked}\n` +
        `Marked Paid: ${result.updated}\n` +
        `Still Pending: ${result.still_pending}\n` +
        (result.errors && result.errors.length ? `Errors: ${result.errors.length}` : '')
      );
      loadData();
    } catch (e) {
      alert('✗ Sync failed: ' + (e.message || 'Unknown'));
    } finally {
      setSyncingPayments(false);
    }
  };

  const handleMarkOrderPaid = async (orderId) => {
    if (!window.confirm('Manually mark this order as PAID? Use this only if you have verified payment was received outside the system.')) return;
    try {
      await api.adminMarkOrderPaid(orderId);
      loadData();
    } catch (e) {
      alert('✗ Failed to mark paid: ' + (e.message || 'Unknown'));
    }
  };

  const handleExportTaxCSV = () => {
    const url = api.adminExportOrdersUrl(true);
    window.open(url, '_blank');
  };

  const openTemplatePreview = async (templateKey) => {
    try {
      setLoading(true);
      const data = await api.adminPreviewTemplateEmail(templateKey);
      setTemplateModal({ key: templateKey, label: data.label, subject: data.subject, html: data.html });
      setTemplateSubject(data.subject);
    } catch (error) {
      console.error('Failed to load template preview:', error);
      alert('✗ FAILED TO LOAD PREVIEW\n\n' + (error.message || ''));
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateSendTest = async () => {
    if (!templateTestEmail || !templateTestEmail.includes('@')) {
      alert('Enter a valid test email address first.');
      return;
    }
    setTemplateSending(true);
    try {
      const result = await api.adminSendTemplateEmailTest(templateModal.key, templateTestEmail, templateSubject);
      if (result.success) {
        alert(`✓ TEST EMAIL SENT\n\nTo: ${result.sent_to}\n\nCheck your inbox!`);
      } else {
        alert('✗ SEND FAILED');
      }
    } catch (error) {
      console.error(error);
      alert('✗ SEND FAILED\n\n' + (error.message || ''));
    } finally {
      setTemplateSending(false);
    }
  };

  const handleTemplateSendBlast = async () => {
    const count = giveawayEntries.length;
    if (!window.confirm(
      `⚠️ BLAST WARNING ⚠️\n\nSend "${templateModal.label}" email to ALL ${count} participants?\n\nSubject: ${templateSubject}\n\nThis runs in the background. You'll see live progress below.`
    )) return;
    setTemplateSending(true);
    setBlastProgress(null);
    try {
      const result = await api.adminSendTemplateEmailBlast(templateModal.key, templateSubject);
      if (!result.success || !result.blast_id) {
        alert('✗ BLAST FAILED TO QUEUE\n\n' + (result.message || 'Unknown error'));
        setTemplateSending(false);
        return;
      }

      // Initialize progress
      setBlastProgress({
        blast_id: result.blast_id,
        status: 'queued',
        total_queued: result.queued,
        sent: 0,
        failed: 0,
        skipped_unsubscribed: result.skipped_unsubscribed || 0,
        failed_emails: [],
      });

      // If nothing to send, short-circuit
      if (!result.queued) {
        setTemplateSending(false);
        alert(`✓ No recipients to send to.\nSkipped (unsubscribed): ${result.skipped_unsubscribed || 0}`);
        return;
      }

      // Poll status every 1.5s until completed
      let attempts = 0;
      const maxAttempts = 120; // up to 3 min
      const poll = async () => {
        attempts += 1;
        try {
          const status = await api.adminGetBlastStatus(result.blast_id);
          setBlastProgress({
            blast_id: status._id,
            status: status.status,
            total_queued: status.total_queued,
            sent: status.sent,
            failed: status.failed,
            skipped_unsubscribed: status.skipped_unsubscribed,
            failed_emails: status.failed_emails || [],
          });
          if (status.status === 'completed' || status.status === 'failed') {
            setTemplateSending(false);
            return;
          }
        } catch (err) {
          console.error('poll error:', err);
        }
        if (attempts < maxAttempts) {
          setTimeout(poll, 1500);
        } else {
          setTemplateSending(false);
        }
      };
      setTimeout(poll, 1200);
    } catch (error) {
      console.error(error);
      alert('✗ BLAST FAILED\n\n' + (error.message || ''));
      setTemplateSending(false);
    }
  };

  const closeTemplateModal = () => {
    if (templateSending) return;
    setTemplateModal(null);
    setBlastProgress(null);
  };

  // ============ CUSTOM EMAIL COMPOSER ============
  const loadCustomAudience = async () => {
    try {
      const data = await api.adminCustomEmailAudienceCount();
      setCustomAudience(data);
    } catch (err) {
      console.error('audience count failed:', err);
    }
  };

  const handleCustomPreview = async () => {
    if (!customSubject.trim() || !customBody.trim()) {
      alert('Subject and body are both required to preview.');
      return;
    }
    try {
      const data = await api.adminPreviewCustomEmail(customSubject.trim(), customBody);
      setCustomPreviewHtml(data.html);
    } catch (err) {
      console.error('preview failed:', err);
      alert('Preview failed: ' + (err.message || 'unknown'));
    }
  };

  const handleCustomTestSend = async () => {
    if (!customSubject.trim() || !customBody.trim()) {
      alert('Subject and body are both required.');
      return;
    }
    const target = (customTestEmail || '').trim();
    if (!target || !target.includes('@')) {
      alert('Please enter a valid test email address.');
      return;
    }
    setCustomSending(true);
    try {
      await api.adminSendCustomEmailTest(customSubject.trim(), customBody, target);
      alert(`✓ TEST SENT\n\nCheck the inbox of ${target}. If it looks good, hit "SEND TO ALL CUSTOMERS" below.`);
    } catch (err) {
      alert('✗ TEST SEND FAILED\n\n' + (err.message || ''));
    } finally {
      setCustomSending(false);
    }
  };

  const handleCustomBlast = async () => {
    if (!customSubject.trim() || !customBody.trim()) {
      alert('Subject and body are both required.');
      return;
    }
    const audienceInfo = customAudience || { sendable: '?', skipped_unsubscribed: 0, total_customers: '?' };
    if (!window.confirm(
      `⚠️ BLAST WARNING ⚠️\n\n` +
      `Send this custom email to ALL ${audienceInfo.sendable} customers?\n\n` +
      `Subject: ${customSubject}\n\n` +
      `(${audienceInfo.skipped_unsubscribed} unsubscribed will be skipped automatically.)\n\n` +
      `This runs in the background. You'll see live progress below.`
    )) return;

    setCustomSending(true);
    setBlastProgress(null);
    try {
      const result = await api.adminSendCustomEmailBlast(customSubject.trim(), customBody);
      if (!result.success || !result.blast_id) {
        alert('✗ BLAST FAILED TO QUEUE\n\n' + (result.message || 'Unknown error'));
        setCustomSending(false);
        return;
      }

      setBlastProgress({
        blast_id: result.blast_id,
        status: 'queued',
        total_queued: result.queued,
        sent: 0,
        failed: 0,
        skipped_unsubscribed: result.skipped_unsubscribed || 0,
        failed_emails: [],
        _label: 'CUSTOM EMAIL',
      });

      if (!result.queued) {
        setCustomSending(false);
        alert(`✓ No recipients to send to.\nSkipped (unsubscribed): ${result.skipped_unsubscribed || 0}`);
        return;
      }

      // Poll status every 1.5s
      let attempts = 0;
      const maxAttempts = 240; // up to 6 min for large blasts
      const poll = async () => {
        attempts += 1;
        try {
          const status = await api.adminGetBlastStatus(result.blast_id);
          setBlastProgress({
            blast_id: status._id,
            status: status.status,
            total_queued: status.total_queued,
            sent: status.sent,
            failed: status.failed,
            skipped_unsubscribed: status.skipped_unsubscribed,
            failed_emails: status.failed_emails || [],
            _label: 'CUSTOM EMAIL',
          });
          if (status.status === 'completed' || status.status === 'failed') {
            setCustomSending(false);
            return;
          }
        } catch (err) {
          console.error('poll error:', err);
        }
        if (attempts < maxAttempts) {
          setTimeout(poll, 1500);
        } else {
          setCustomSending(false);
        }
      };
      setTimeout(poll, 1200);
    } catch (err) {
      console.error(err);
      alert('✗ BLAST FAILED\n\n' + (err.message || ''));
      setCustomSending(false);
    }
  };
  // ============ /CUSTOM EMAIL COMPOSER ============

  // ============ SHOPIFY SYNC ============
  const handleShopifySyncPreview = async () => {
    setShopifySyncing(true);
    setShopifySyncResult(null);
    try {
      const preview = await api.adminShopifySyncPreview();
      setShopifySyncResult({ mode: 'preview', ...preview });
    } catch (err) {
      alert('✗ SHOPIFY PREVIEW FAILED\n\n' + (err.message || ''));
    } finally {
      setShopifySyncing(false);
    }
  };

  const handleShopifySyncApply = async () => {
    // Confirm before touching the DB.
    let willImportCount = null;
    if (shopifySyncResult && shopifySyncResult.mode === 'preview') {
      willImportCount = (shopifySyncResult.new_products || []).length;
    }
    const msg = willImportCount !== null
      ? `Import ${willImportCount} new product(s) from Shopify as HIDDEN (visible=false) for review?`
      : 'Sync from Shopify? New products will be imported as HIDDEN so you can review before flipping live.';
    if (!window.confirm(msg)) return;

    setShopifySyncing(true);
    try {
      const result = await api.adminShopifySyncApply();
      setShopifySyncResult({ mode: 'applied', ...result });
      await loadData();  // refresh product list
    } catch (err) {
      alert('✗ SHOPIFY SYNC FAILED\n\n' + (err.message || ''));
    } finally {
      setShopifySyncing(false);
    }
  };
  // ============ /SHOPIFY SYNC ============


  const handlePinSubmit = async (e) => {
    e.preventDefault();
    try {
      const result = await api.verifyAdminPin(pin);
      if (result.success) {
        setAuthenticated(true);
        setError('');
        loadData();
      }
    } catch (error) {
      setError('INVALID PIN');
      setPin('');
    }
  };

  const loadData = async () => {
    setLoading(true);
    // Use Promise.allSettled so a single failing endpoint doesn't blank out the whole admin.
    // Each section loads independently; failures log but preserve other sections' data.
    const [
      productsRes,
      ordersRes,
      userProfilesRes,
      collectionsRes,
      revenueRes,
      newsletterRes,
      giveawayRes,
    ] = await Promise.allSettled([
      api.adminGetProducts(true),
      api.adminGetOrders(),
      api.adminGetUserProfiles(),
      api.adminGetLookbookCollections(),
      api.adminGetRevenue(),
      api.adminGetNewsletterSubscribers(),
      api.adminGetGiveawayEntries(),
    ]);

    const apply = (res, setter, label) => {
      if (res.status === 'fulfilled') {
        setter(res.value ?? []);
      } else {
        console.error(`[admin] Failed to load ${label}:`, res.reason);
      }
    };

    apply(productsRes, setProducts, 'products');
    apply(ordersRes, setOrders, 'orders');
    apply(userProfilesRes, setCustomers, 'members/customers');
    apply(collectionsRes, setCollections, 'lookbook');
    apply(revenueRes, (v) => setRevenue(v ?? {}), 'revenue');
    apply(newsletterRes, setNewsletterSubscribers, 'newsletter');
    apply(giveawayRes, setGiveawayEntries, 'giveaway');

    setLoading(false);
  };

  const toggleProductVisibility = async (productId) => {
    try {
      await api.adminToggleProductVisibility(productId);
      await loadData();
    } catch (error) {
      console.error('Error toggling visibility:', error);
    }
  };

  const handleDeleteProduct = async (product) => {
    const name = (product && product.name) || 'this product';
    if (!window.confirm(`DELETE "${name}" permanently?\n\nThis removes it from the catalog for everyone. This action cannot be undone.\n\nTip: Use the 👁️ button to hide a product without deleting it.`)) {
      return;
    }
    try {
      await api.adminDeleteProduct(product._id);
      await loadData();
    } catch (error) {
      console.error('Error deleting product:', error);
      alert('✗ FAILED TO DELETE PRODUCT\n\n' + (error?.message || 'Please try again.'));
    }
  };

  const handleSyncNewsletterToGiveaway = async () => {
    if (!window.confirm('Sync all newsletter subscribers to giveaway? This will add any subscribers not already in the giveaway.')) {
      return;
    }
    
    setLoading(true);
    try {
      const result = await api.adminSyncNewsletterToGiveaway();
      alert(`✓ SYNC COMPLETE\n\nAdded: ${result.added}\nSkipped (already in giveaway): ${result.skipped}\nTotal processed: ${result.total_processed}`);
      await loadData();
    } catch (error) {
      console.error('Error syncing newsletter to giveaway:', error);
      alert('✗ SYNC FAILED');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLookbook = () => {
    setEditingLookbook(null);
    setLookbookForm({
      name: '',
      season: '',
      description: '',
      product_ids: [],
      banner_color: '#F7F6D8',
      emoji_icons: ['📖'],
      image_url: ''
    });
    setShowLookbookForm(true);
  };

  const handleEditLookbook = async (collectionId) => {
    try {
      const collection = await api.adminGetLookbookCollection(collectionId);
      setEditingLookbook(collectionId);
      setLookbookForm({
        name: collection.name || '',
        season: collection.season || '',
        description: collection.description || '',
        product_ids: collection.product_ids || [],
        banner_color: collection.banner_color || '#F7F6D8',
        emoji_icons: collection.emoji_icons || ['📖'],
        image_url: collection.image_url || ''
      });
      setShowLookbookForm(true);
    } catch (error) {
      console.error('Error loading lookbook:', error);
    }
  };

  const handleSaveLookbook = async () => {
    try {
      if (editingLookbook) {
        await api.adminUpdateLookbook(editingLookbook, lookbookForm);
      } else {
        await api.adminCreateLookbook(lookbookForm);
      }
      setShowLookbookForm(false);
      setEditingLookbook(null);
      await loadData();
    } catch (error) {
      console.error('Error saving lookbook:', error);
      alert('Failed to save lookbook');
    }
  };

  const handleDeleteLookbook = async (collectionId) => {
    if (!window.confirm('Delete this lookbook collection? This cannot be undone.')) {
      return;
    }
    
    try {
      await api.adminDeleteLookbook(collectionId);
      await loadData();
    } catch (error) {
      console.error('Error deleting lookbook:', error);
    }
  };

  const toggleProductInLookbook = (productId) => {
    setLookbookForm(prev => ({
      ...prev,
      product_ids: prev.product_ids.includes(productId)
        ? prev.product_ids.filter(id => id !== productId)
        : [...prev.product_ids, productId]
    }));
  };

  if (!authenticated) {
    return (
      <div className="crt" style={{ minHeight: '100vh', background: '#0a0a0a', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <div style={{ textAlign: 'center', maxWidth: '400px', padding: '20px' }}>
          <img src="/logo.png" alt="Admin" style={{ width: '100px', height: '100px', imageRendering: 'pixelated', marginBottom: '30px' }} />
          <h1 style={{ color: '#4AE54A', fontSize: '24px', marginBottom: '20px', fontFamily: 'Press Start 2P, monospace' }}>ADMIN ACCESS</h1>
          <p style={{ color: '#FFC857', fontSize: '12px', marginBottom: '30px' }}>ENTER PIN CODE</p>
          
          <form onSubmit={handlePinSubmit}>
            <input
              type="password"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              maxLength={4}
              className="retro-input"
              style={{ 
                background: '#000', 
                border: '3px solid #4AE54A', 
                color: '#4AE54A', 
                fontSize: '32px', 
                padding: '20px', 
                width: '100%',
                textAlign: 'center',
                letterSpacing: '0.5em',
                fontFamily: 'IBM Plex Mono, monospace'
              }}
              placeholder="••••"
              autoFocus
            />
            
            {error && <p style={{ color: '#FF5A5F', fontSize: '12px', marginTop: '15px' }}>{error}</p>}
            
            <button
              type="submit"
              disabled={pin.length !== 4}
              className="retro-btn"
              style={{ 
                marginTop: '30px', 
                background: '#2C6E49', 
                color: '#FEFEE3', 
                padding: '15px 40px',
                fontSize: '14px',
                opacity: pin.length === 4 ? 1 : 0.5,
                cursor: pin.length === 4 ? 'pointer' : 'not-allowed'
              }}
            >
              ENTER &gt;&gt;&gt;
            </button>
          </form>
          
          <button
            onClick={() => navigate('/')}
            style={{ marginTop: '30px', background: 'none', border: 'none', color: '#666', fontSize: '11px', cursor: 'pointer' }}
          >
            BACK TO SHOP
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#FEFEE3' }}>
      <div style={{ background: '#FEFEE3', padding: '20px', borderBottom: '3px solid #000' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <h1 style={{ fontSize: '24px', color: '#2C2C2C', marginBottom: '5px' }}>ADMIN PANEL</h1>
          <p style={{ fontSize: '12px', color: '#4C956C' }}>
            {activeTab === 'products' && `${products.length} PRODUCTS`}
            {activeTab === 'orders' && `${orders.length} ORDERS`}
            {activeTab === 'members' && `${customers.length} MEMBERS`}
            {activeTab === 'lookbook' && `${collections.length} COLLECTIONS`}
            {activeTab === 'newsletter' && `${newsletterSubscribers.length} SUBSCRIBERS`}
            {activeTab === 'giveaway' && `${giveawayEntries.length}/50 ENTRIES`}
            {activeTab === 'players' && `${playerStatsTotal} SELECTIONS`}
          </p>
        </div>
      </div>
      
      <div style={{ background: '#FEFEE3', borderBottom: '3px solid #000' }}>
        {/* Horizontally scrollable tab strip — keeps the whole admin page
            within the viewport on mobile and lets the tabs themselves scroll
            sideways. Buttons are flex-shrink:0 so they don't squish. */}
        <div
          className="hide-scrollbar allow-x-pan"
          style={{
            maxWidth: '1400px',
            margin: '0 auto',
            display: 'flex',
            gap: '0',
            overflowX: 'auto',
            overflowY: 'hidden',
            WebkitOverflowScrolling: 'touch',
            scrollSnapType: 'x proximity',
          }}
        >
          {[
            { key: 'products',   label: '🏷️ PRODUCTS' },
            { key: 'orders',     label: '📋 ORDERS' },
            { key: 'lookbook',   label: '📖 LOOKBOOK' },
            { key: 'members',    label: '👥 MEMBERS' },
            { key: 'sim',        label: '🎮 SIM' },
            { key: 'newsletter', label: '📧 NEWSLETTER' },
            { key: 'giveaway',   label: '🎁 GIVEAWAY' },
            { key: 'players',    label: '👥 PLAYERS', testid: 'admin-tab-players' },
          ].map((t, idx, arr) => {
            const isActive = activeTab === t.key;
            const isLast = idx === arr.length - 1;
            return (
              <button
                key={t.key}
                onClick={() => {
                  setActiveTab(t.key);
                  if (t.key === 'players') loadPlayerStats();
                  if (t.key === 'newsletter') loadCustomAudience();
                }}
                data-testid={t.testid}
                style={{
                  flex: '0 0 auto',
                  minWidth: '120px',
                  padding: '15px 20px',
                  background: isActive ? '#2C6E49' : '#CCC',
                  color: isActive ? '#FEFEE3' : '#666',
                  border: 'none',
                  borderRight: isLast ? 'none' : '2px solid #000',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  scrollSnapAlign: 'start',
                }}
              >
                {t.label}
              </button>
            );
          })}
        </div>
      </div>
      
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '20px' }}>
        {loading ? (
          <p style={{ textAlign: 'center', color: '#4C956C' }}>LOADING...</p>
        ) : (
          <>
            {activeTab === 'products' && (
              <div>
                {/* Toolbar: ADD + SYNC + filter chips */}
                <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
                  <button
                    onClick={() => navigate('/admin/products/add')}
                    className="retro-btn"
                    style={{
                      background: '#2C6E49', color: '#FEFEE3', padding: '12px 24px',
                      fontSize: '12px', border: '3px solid #000', boxShadow: '4px 4px 0 0 #000',
                    }}
                    data-testid="add-product-button"
                  >
                    + ADD PRODUCT
                  </button>
                  <button
                    onClick={handleShopifySyncPreview}
                    disabled={shopifySyncing}
                    data-testid="shopify-sync-preview-btn"
                    style={{
                      background: shopifySyncing ? '#AAA' : '#111',
                      color: '#4AE54A', padding: '12px 20px',
                      fontSize: '11px', border: '3px solid #4AE54A', boxShadow: '4px 4px 0 0 #000',
                      fontWeight: 'bold', letterSpacing: '0.1em',
                      cursor: shopifySyncing ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {shopifySyncing ? '⏳ CHECKING…' : '↻ CHECK SHOPIFY'}
                  </button>
                  <button
                    onClick={handleShopifySyncApply}
                    disabled={shopifySyncing}
                    data-testid="shopify-sync-apply-btn"
                    style={{
                      background: shopifySyncing ? '#AAA' : '#4AE54A',
                      color: '#000', padding: '12px 20px',
                      fontSize: '11px', border: '3px solid #000', boxShadow: '4px 4px 0 0 #000',
                      fontWeight: 'bold', letterSpacing: '0.1em',
                      cursor: shopifySyncing ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {shopifySyncing ? '⏳ SYNCING…' : '⇩ SYNC FROM SHOPIFY'}
                  </button>

                  <div style={{ flex: 1, minWidth: '20px' }} />

                  {/* Visibility filter */}
                  {['all', 'hidden', 'visible'].map((f) => (
                    <button
                      key={f}
                      onClick={() => setProductVisibilityFilter(f)}
                      data-testid={`product-filter-${f}`}
                      style={{
                        background: productVisibilityFilter === f ? '#2C6E49' : '#FFF',
                        color:      productVisibilityFilter === f ? '#FEFEE3' : '#2C6E49',
                        padding: '10px 14px', fontSize: '11px',
                        border: '2px solid #000', fontWeight: 'bold', letterSpacing: '0.08em',
                        cursor: 'pointer',
                      }}
                    >
                      {f === 'all' ? 'ALL' : f === 'hidden' ? '👁‍🗨 HIDDEN' : '● LIVE'}
                    </button>
                  ))}
                </div>

                {/* Sync result panel */}
                {shopifySyncResult && (
                  <div
                    data-testid="shopify-sync-result"
                    style={{
                      background: '#0F0F0F', color: '#FEFEE3', padding: '16px',
                      marginBottom: '20px', border: '3px solid #4AE54A', boxShadow: '4px 4px 0 0 #000',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <span style={{ color: '#FFC857', letterSpacing: '0.15em', fontSize: '12px', fontWeight: 'bold' }}>
                        SHOPIFY {shopifySyncResult.mode === 'applied' ? 'SYNC RESULT' : 'DIFF PREVIEW'}
                      </span>
                      <button
                        onClick={() => setShopifySyncResult(null)}
                        style={{
                          background: '#FF5A5F', color: '#FFF', padding: '4px 8px',
                          border: '2px solid #000', fontSize: '10px', cursor: 'pointer', fontWeight: 'bold',
                        }}
                      >✕</button>
                    </div>
                    <p style={{ fontSize: '11px', color: '#CCCCCC', marginBottom: '10px' }}>
                      Shopify total: <strong style={{ color: '#4AE54A' }}>{shopifySyncResult.shopify_total}</strong> •
                      &nbsp;Already synced: <strong style={{ color: '#4AE54A' }}>{shopifySyncResult.already_in_db}</strong> •
                      &nbsp;Skipped (title clash): <strong style={{ color: '#FFC857' }}>{(shopifySyncResult.skipped_title_duplicates || shopifySyncResult.title_duplicates || []).length}</strong> •
                      &nbsp;{shopifySyncResult.mode === 'applied' ? 'Imported' : 'To import'}:
                      &nbsp;<strong style={{ color: '#4AE54A' }}>{(shopifySyncResult.imported || shopifySyncResult.new_products || []).length}</strong>
                    </p>

                    {(shopifySyncResult.imported || shopifySyncResult.new_products || []).length > 0 && (
                      <details open style={{ fontSize: '11px', color: '#4AE54A', marginTop: '8px' }}>
                        <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>
                          {shopifySyncResult.mode === 'applied' ? 'IMPORTED' : 'WILL IMPORT'} ({(shopifySyncResult.imported || shopifySyncResult.new_products || []).length})
                        </summary>
                        <ul style={{ marginTop: '6px', color: '#DDDDDD', lineHeight: 1.6, paddingLeft: '20px' }}>
                          {(shopifySyncResult.imported || shopifySyncResult.new_products || []).map((p) => (
                            <li key={p.shopify_id}>
                              <span style={{ color: '#4AE54A' }}>{p.title}</span>
                              &nbsp;— ${p.price?.toFixed?.(2) || p.price} · {p.category_guess} · {p.variants}v / {p.images}img
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}
                    {(shopifySyncResult.skipped_title_duplicates || shopifySyncResult.title_duplicates || []).length > 0 && (
                      <details style={{ fontSize: '11px', color: '#FFC857', marginTop: '8px' }}>
                        <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>
                          SKIPPED — SAME TITLE AS EXISTING PRODUCT ({(shopifySyncResult.skipped_title_duplicates || shopifySyncResult.title_duplicates || []).length})
                        </summary>
                        <ul style={{ marginTop: '6px', color: '#CCCCCC', lineHeight: 1.6, paddingLeft: '20px' }}>
                          {(shopifySyncResult.skipped_title_duplicates || shopifySyncResult.title_duplicates || []).map((p) => (
                            <li key={p.shopify_id}>{p.title} <span style={{ color: '#666' }}>(shopify_id={p.shopify_id})</span></li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </div>
                )}

                {products
                  .filter((product) => {
                    if (productVisibilityFilter === 'hidden')  return !product.visible;
                    if (productVisibilityFilter === 'visible') return !!product.visible;
                    return true;
                  })
                  .map((product) => (
                  <div key={product._id} className="retro-card" style={{ background: '#FFF', padding: '15px', marginBottom: '10px', display: 'flex', gap: '15px', alignItems: 'center' }}>
                    <div style={{ width: '60px', height: '60px', border: '2px solid #000', background: '#F7F6D8', flexShrink: 0 }}>
                      {product.images && product.images[0] && <img src={product.images[0]} alt={product.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                    </div>
                    
                    <div style={{ flex: 1 }}>
                      <h3 style={{ fontSize: '14px', color: product.visible ? '#4C956C' : '#999', fontWeight: 'bold', marginBottom: '3px', textDecoration: product.visible ? 'none' : 'line-through' }}>
                        {product.name}
                      </h3>
                      <p style={{ fontSize: '11px', color: '#4C956C' }}>
                        {product.category} · ${product.base_price.toFixed(2)} · {product.variants?.length || 0} colors
                      </p>
                    </div>
                    
                    {!product.visible && (
                      <span style={{ background: '#FF5A5F', color: '#FFF', padding: '4px 12px', fontSize: '10px', fontWeight: 'bold' }}>HIDDEN</span>
                    )}
                    
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button
                        onClick={() => toggleProductVisibility(product._id)}
                        className="retro-btn"
                        style={{ background: '#FFF', border: '2px solid #000', padding: '8px 12px', fontSize: '11px' }}
                      >
                        👁️
                      </button>
                      <button 
                        onClick={() => navigate(`/admin/products/${product._id}/edit`)}
                        className="retro-btn" 
                        style={{ background: '#FFF', border: '2px solid #2C6E49', padding: '8px 12px', fontSize: '11px' }}
                        data-testid={`edit-product-${product._id}`}
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleDeleteProduct(product)}
                        data-testid={`delete-product-${product._id}`}
                        className="retro-btn"
                        title={`Delete ${product.name}`}
                        style={{ background: '#FFF', border: '2px solid #FF5A5F', padding: '8px 12px', fontSize: '11px', cursor: 'pointer' }}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {activeTab === 'orders' && (
              <div>
                <div className="retro-card" style={{ background: '#FFF', padding: '20px', marginBottom: '20px' }}>
                  <h3 style={{ fontSize: '14px', color: '#2C6E49', marginBottom: '15px' }}>REVENUE DASHBOARD</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '15px' }}>
                    <div>
                      <p style={{ fontSize: '10px', color: '#4C956C', marginBottom: '5px' }}>TOTAL REVENUE</p>
                      <p style={{ fontSize: '20px', color: '#2C2C2C', fontWeight: 'bold' }}>${revenue.total_revenue?.toFixed(2) || '0.00'}</p>
                    </div>
                    <div>
                      <p style={{ fontSize: '10px', color: '#4C956C', marginBottom: '5px' }}>TOTAL ORDERS</p>
                      <p style={{ fontSize: '20px', color: '#2C2C2C', fontWeight: 'bold' }}>{revenue.total_orders || 0}</p>
                    </div>
                    <div>
                      <p style={{ fontSize: '10px', color: '#4C956C', marginBottom: '5px' }}>AVG ORDER VALUE</p>
                      <p style={{ fontSize: '20px', color: '#2C2C2C', fontWeight: 'bold' }}>${revenue.avg_order_value?.toFixed(2) || '0.00'}</p>
                    </div>
                    <div>
                      <p style={{ fontSize: '10px', color: '#4C956C', marginBottom: '5px' }}>ITEMS SOLD</p>
                      <p style={{ fontSize: '20px', color: '#2C2C2C', fontWeight: 'bold' }}>{revenue.items_sold || 0}</p>
                    </div>
                  </div>

                  {/* Revenue Actions: Sync + Tax Export */}
                  <div style={{ display: 'flex', gap: '10px', marginTop: '18px', paddingTop: '18px', borderTop: '2px dashed #E0E0E0', flexWrap: 'wrap' }}>
                    <button
                      onClick={handleSyncPayments}
                      disabled={syncingPayments}
                      data-testid="sync-payments-btn"
                      style={{
                        background: syncingPayments ? '#AAA' : '#2C6E49',
                        color: '#FEFEE3',
                        padding: '10px 18px',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        border: '3px solid #000',
                        boxShadow: '3px 3px 0 0 #000',
                        cursor: syncingPayments ? 'not-allowed' : 'pointer',
                        letterSpacing: '1.5px',
                        fontFamily: "'Courier New', monospace"
                      }}
                    >
                      {syncingPayments ? '⏳ SYNCING...' : '🔄 SYNC PAYMENTS'}
                    </button>
                    <button
                      onClick={handleExportTaxCSV}
                      data-testid="export-tax-csv-btn"
                      style={{
                        background: '#FFC857',
                        color: '#0a0a0a',
                        padding: '10px 18px',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        border: '3px solid #000',
                        boxShadow: '3px 3px 0 0 #000',
                        cursor: 'pointer',
                        letterSpacing: '1.5px',
                        fontFamily: "'Courier New', monospace"
                      }}
                    >
                      📊 EXPORT TAX REPORT (CSV)
                    </button>
                  </div>
                  <p style={{ fontSize: '10px', color: '#888', marginTop: '10px', lineHeight: 1.6 }}>
                    <strong>SYNC PAYMENTS</strong>: Checks all pending orders against Stripe/PayPal and marks them paid if the provider confirms. Run after noticing a stuck order.<br/>
                    <strong>EXPORT TAX REPORT</strong>: Downloads a full CSV of every order (paid + pending) with dates, totals, items, and addresses — tax-season ready.
                  </p>
                </div>
                
                {orders.length === 0 ? (
                  <p style={{ textAlign: 'center', color: '#4C956C' }}>NO ORDERS YET</p>
                ) : (
                  orders.map((order) => (
                    <div key={order._id} className="retro-card" style={{ background: '#FFF', padding: '20px', marginBottom: '15px', border: '3px solid #000', boxShadow: '4px 4px 0 0 #000' }}>
                      {/* Order Header */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', paddingBottom: '15px', borderBottom: '2px solid #E0E0E0' }}>
                        <div style={{ flex: 1 }}>
                          <p style={{ fontSize: '14px', color: '#2C6E49', fontWeight: 'bold', marginBottom: '5px' }}>
                            ORDER #{order._id.slice(-8).toUpperCase()}
                          </p>
                          <p style={{ fontSize: '11px', color: '#4C956C', marginBottom: '3px' }}>
                            📧 {order.email}
                          </p>
                          <p style={{ fontSize: '10px', color: '#999' }}>
                            📅 {new Date(order.created_at).toLocaleString()}
                          </p>
                        </div>
                        
                        <div style={{ textAlign: 'right' }}>
                          <p style={{ fontSize: '18px', color: '#2C2C2C', fontWeight: 'bold', marginBottom: '5px' }}>
                            ${order.total.toFixed(2)}
                          </p>
                          <span style={{ 
                            background: order.status === 'processing' ? '#FFC857' : order.status === 'shipped' ? '#2C6E49' : '#4C956C',
                            color: '#FFF',
                            padding: '4px 12px',
                            fontSize: '10px',
                            fontWeight: 'bold',
                            borderRadius: '3px',
                            display: 'inline-block'
                          }}>
                            {order.status.toUpperCase()}
                          </span>
                        </div>
                      </div>

                      {/* Products Ordered */}
                      <div style={{ marginBottom: '20px' }}>
                        <h4 style={{ fontSize: '12px', color: '#2C6E49', fontWeight: 'bold', marginBottom: '12px', letterSpacing: '0.05em' }}>
                          🛍️ PRODUCTS ORDERED
                        </h4>
                        
                        <div style={{ background: '#FEFEE3', border: '2px solid #000', padding: '12px' }}>
                          {order.items && order.items.length > 0 ? (
                            order.items.map((item, index) => (
                              <div 
                                key={item.variant_id || item.product_id || `${item.name || 'item'}-${index}`} 
                                style={{ 
                                  display: 'flex', 
                                  gap: '12px', 
                                  marginBottom: index < order.items.length - 1 ? '12px' : '0',
                                  paddingBottom: index < order.items.length - 1 ? '12px' : '0',
                                  borderBottom: index < order.items.length - 1 ? '1px solid #D0D0D0' : 'none',
                                  alignItems: 'center'
                                }}
                              >
                                {/* Product Image */}
                                <div style={{ 
                                  width: '60px', 
                                  height: '60px', 
                                  border: '2px solid #000', 
                                  background: '#FFF',
                                  flexShrink: 0,
                                  overflow: 'hidden'
                                }}>
                                  {item.image_url && (
                                    <img 
                                      src={item.image_url} 
                                      alt={item.name} 
                                      style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                                    />
                                  )}
                                </div>
                                
                                {/* Product Details */}
                                <div style={{ flex: 1 }}>
                                  <p style={{ fontSize: '12px', color: '#2C2C2C', fontWeight: 'bold', marginBottom: '4px' }}>
                                    {item.name}
                                  </p>
                                  <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                                    <p style={{ fontSize: '10px', color: '#4C956C' }}>
                                      <strong>Color:</strong> {item.color}
                                    </p>
                                    <p style={{ fontSize: '10px', color: '#4C956C' }}>
                                      <strong>Size:</strong> {item.size}
                                    </p>
                                    <p style={{ fontSize: '10px', color: '#4C956C' }}>
                                      <strong>Qty:</strong> {item.quantity}
                                    </p>
                                    <p style={{ fontSize: '10px', color: '#2C2C2C', fontWeight: 'bold' }}>
                                      ${item.price.toFixed(2)} each
                                    </p>
                                  </div>
                                </div>
                                
                                {/* Item Subtotal */}
                                <div style={{ textAlign: 'right', minWidth: '70px' }}>
                                  <p style={{ fontSize: '13px', color: '#2C2C2C', fontWeight: 'bold' }}>
                                    ${(item.price * item.quantity).toFixed(2)}
                                  </p>
                                </div>
                              </div>
                            ))
                          ) : (
                            <p style={{ fontSize: '11px', color: '#999', textAlign: 'center', padding: '10px' }}>
                              No items found
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Shipping Address */}
                      <div style={{ marginBottom: '15px' }}>
                        <h4 style={{ fontSize: '12px', color: '#2C6E49', fontWeight: 'bold', marginBottom: '10px', letterSpacing: '0.05em' }}>
                          📦 SHIPPING ADDRESS
                        </h4>
                        
                        {order.shipping_address ? (
                          <div style={{ 
                            background: '#F7F6D8', 
                            border: '2px solid #000', 
                            padding: '12px',
                            fontSize: '11px',
                            color: '#2C2C2C',
                            lineHeight: '1.6'
                          }}>
                            <p style={{ fontWeight: 'bold', marginBottom: '5px' }}>{order.shipping_address.name}</p>
                            <p>{order.shipping_address.address}</p>
                            <p>{order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zip_code}</p>
                            <p style={{ marginTop: '5px' }}>📞 {order.shipping_address.phone}</p>
                          </div>
                        ) : (
                          <p style={{ fontSize: '11px', color: '#999', fontStyle: 'italic' }}>
                            No shipping address provided
                          </p>
                        )}
                      </div>

                      {/* Order Actions */}
                      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', paddingTop: '10px', borderTop: '2px solid #E0E0E0' }}>
                        <select
                          value={order.status}
                          onChange={async (e) => {
                            try {
                              await api.adminUpdateOrderStatus(order._id, e.target.value);
                              await loadData();
                            } catch (error) {
                              console.error('Error updating order status:', error);
                            }
                          }}
                          style={{
                            padding: '8px 12px',
                            fontSize: '11px',
                            border: '2px solid #000',
                            background: '#FFF',
                            fontWeight: 'bold',
                            cursor: 'pointer'
                          }}
                        >
                          <option value="pending">Pending</option>
                          <option value="processing">Processing</option>
                          <option value="shipped">Shipped</option>
                          <option value="delivered">Delivered</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                        
                        <button
                          onClick={async () => {
                            if (window.confirm(`Are you sure you want to DELETE order #${order._id.slice(-8).toUpperCase()}?\n\nThis action cannot be undone.`)) {
                              try {
                                await api.adminDeleteOrder(order._id);
                                await loadData();
                                alert('Order deleted successfully');
                              } catch (error) {
                                console.error('Error deleting order:', error);
                                alert('Failed to delete order');
                              }
                            }
                          }}
                          style={{
                            padding: '8px 16px',
                            fontSize: '11px',
                            border: '2px solid #000',
                            background: '#FF4444',
                            color: '#FFF',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = '#CC0000';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = '#FF4444';
                          }}
                        >
                          🗑️ DELETE
                        </button>
                        {order.payment_status !== 'paid' && (
                          <button
                            onClick={() => handleMarkOrderPaid(order._id)}
                            data-testid={`mark-paid-${order._id}`}
                            style={{
                              padding: '8px 16px',
                              fontSize: '11px',
                              border: '2px solid #000',
                              background: '#2C6E49',
                              color: '#FEFEE3',
                              fontWeight: 'bold',
                              cursor: 'pointer',
                              transition: 'background-color 150ms',
                              marginLeft: '6px'
                            }}
                            title="Manually mark this order as paid"
                          >
                            ✅ MARK PAID
                          </button>
                        )}
                        {order.payment_status !== 'paid' && (
                          <button
                            onClick={() => openRetryPaymentModal(order)}
                            data-testid={`retry-payment-${order._id}`}
                            style={{
                              padding: '8px 16px',
                              fontSize: '11px',
                              border: '2px solid #000',
                              background: '#FFC857',
                              color: '#0a0a0a',
                              fontWeight: 'bold',
                              cursor: 'pointer',
                              transition: 'background-color 150ms',
                              marginLeft: '6px'
                            }}
                            title="Generate a fresh Stripe payment link to charge this order - open yourself (card over phone) or email to customer"
                          >
                            💳 RETRY PAYMENT
                          </button>
                        )}
                        <span style={{
                          marginLeft: '10px',
                          padding: '4px 10px',
                          fontSize: '10px',
                          fontWeight: 'bold',
                          letterSpacing: '1px',
                          background: order.payment_status === 'paid' ? '#4AE54A' : '#FFC857',
                          color: '#000',
                          borderRadius: '3px'
                        }}>
                          💳 {(order.payment_status || 'pending').toUpperCase()}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
            
            {activeTab === 'lookbook' && (
              <div>
                {!showLookbookForm ? (
                  <>
                    <button
                      onClick={handleCreateLookbook}
                      className="retro-btn"
                      style={{
                        background: '#4AE54A',
                        color: '#0a0a0a',
                        padding: '12px 24px',
                        fontSize: '12px',
                        marginBottom: '20px',
                        fontWeight: 'bold',
                        border: '3px solid #000',
                        boxShadow: '4px 4px 0 0 #000'
                      }}
                      data-testid="create-lookbook-btn"
                    >
                      + CREATE NEW COLLECTION
                    </button>
                    
                    {collections.length === 0 ? (
                      <p style={{ textAlign: 'center', color: '#4C956C', marginTop: '40px' }}>NO COLLECTIONS YET</p>
                    ) : (
                      collections.map((collection) => (
                        <div key={collection._id} className="retro-card" style={{ background: '#FFF', padding: '15px', marginBottom: '10px', display: 'flex', gap: '15px', alignItems: 'center' }}>
                          <div style={{ width: '80px', height: '80px', border: '2px solid #000', background: collection.banner_color || '#F7F6D8', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <span style={{ fontSize: '32px' }}>{collection.emoji_icons?.[0] || '📖'}</span>
                          </div>
                          
                          <div style={{ flex: 1 }}>
                            <h3 style={{ fontSize: '14px', color: '#2C2C2C', fontWeight: 'bold', marginBottom: '3px' }}>{collection.name}</h3>
                            <p style={{ fontSize: '11px', color: '#4C956C' }}>{collection.season} · {collection.product_ids?.length || 0} items</p>
                            <p style={{ fontSize: '10px', color: '#999', marginTop: '3px' }}>{collection.description}</p>
                          </div>
                          
                          <div style={{ display: 'flex', gap: '10px' }}>
                            <button 
                              onClick={() => handleEditLookbook(collection._id)}
                              className="retro-btn" 
                              style={{ background: '#FFF', border: '2px solid #2C6E49', color: '#2C6E49', padding: '8px 12px', fontSize: '11px', fontWeight: 'bold' }}
                              data-testid={`edit-lookbook-${collection._id}`}
                            >
                              ✏️ EDIT
                            </button>
                            <button 
                              onClick={() => handleDeleteLookbook(collection._id)}
                              className="retro-btn" 
                              style={{ background: '#FFF', border: '2px solid #FF5A5F', color: '#FF5A5F', padding: '8px 12px', fontSize: '11px', fontWeight: 'bold' }}
                              data-testid={`delete-lookbook-${collection._id}`}
                            >
                              🗑️ DELETE
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </>
                ) : (
                  <div style={{ background: '#FFF', padding: '20px', border: '3px solid #000', boxShadow: '4px 4px 0 0 #000' }}>
                    <h2 style={{ fontSize: '16px', color: '#2C6E49', marginBottom: '20px', fontWeight: 'bold' }}>
                      {editingLookbook ? '[ EDIT COLLECTION ]' : '[ NEW COLLECTION ]'}
                    </h2>
                    
                    <div style={{ marginBottom: '15px' }}>
                      <label style={{ fontSize: '11px', color: '#4C956C', fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>COLLECTION NAME</label>
                      <input
                        type="text"
                        value={lookbookForm.name}
                        onChange={(e) => setLookbookForm({...lookbookForm, name: e.target.value})}
                        placeholder="e.g., Spring 2025 Essentials"
                        className="retro-input"
                        style={{ width: '100%', padding: '10px', fontSize: '12px', border: '2px solid #000', background: '#FEFEE3' }}
                      />
                    </div>
                    
                    <div style={{ marginBottom: '15px' }}>
                      <label style={{ fontSize: '11px', color: '#4C956C', fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>SEASON</label>
                      <input
                        type="text"
                        value={lookbookForm.season}
                        onChange={(e) => setLookbookForm({...lookbookForm, season: e.target.value})}
                        placeholder="e.g., Spring 2025, Summer Collection"
                        className="retro-input"
                        style={{ width: '100%', padding: '10px', fontSize: '12px', border: '2px solid #000', background: '#FEFEE3' }}
                      />
                    </div>
                    
                    <div style={{ marginBottom: '15px' }}>
                      <label style={{ fontSize: '11px', color: '#4C956C', fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>DESCRIPTION</label>
                      <textarea
                        value={lookbookForm.description}
                        onChange={(e) => setLookbookForm({...lookbookForm, description: e.target.value})}
                        placeholder="Describe this collection..."
                        className="retro-input"
                        rows={3}
                        style={{ width: '100%', padding: '10px', fontSize: '12px', border: '2px solid #000', background: '#FEFEE3', resize: 'vertical' }}
                      />
                    </div>
                    
                    <div style={{ marginBottom: '15px' }}>
                      <label style={{ fontSize: '11px', color: '#4C956C', fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>BANNER COLOR</label>
                      <input
                        type="color"
                        value={lookbookForm.banner_color}
                        onChange={(e) => setLookbookForm({...lookbookForm, banner_color: e.target.value})}
                        style={{ width: '100px', height: '40px', border: '2px solid #000', cursor: 'pointer' }}
                      />
                      <input
                        type="text"
                        value={lookbookForm.banner_color}
                        onChange={(e) => setLookbookForm({...lookbookForm, banner_color: e.target.value})}
                        placeholder="#F7F6D8"
                        className="retro-input"
                        style={{ width: '120px', padding: '8px', fontSize: '11px', border: '2px solid #000', background: '#FEFEE3', marginLeft: '10px' }}
                      />
                    </div>
                    
                    <div style={{ marginBottom: '15px' }}>
                      <label style={{ fontSize: '11px', color: '#4C956C', fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>EMOJI ICON</label>
                      <input
                        type="text"
                        value={lookbookForm.emoji_icons[0] || ''}
                        onChange={(e) => setLookbookForm({...lookbookForm, emoji_icons: [e.target.value]})}
                        placeholder="📖"
                        className="retro-input"
                        style={{ width: '100px', padding: '10px', fontSize: '20px', border: '2px solid #000', background: '#FEFEE3', textAlign: 'center' }}
                      />
                    </div>
                    
                    <div style={{ marginBottom: '20px' }}>
                      <label style={{ fontSize: '11px', color: '#4C956C', fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>IMAGE URL (Optional)</label>
                      <input
                        type="text"
                        value={lookbookForm.image_url}
                        onChange={(e) => setLookbookForm({...lookbookForm, image_url: e.target.value})}
                        placeholder="https://..."
                        className="retro-input"
                        style={{ width: '100%', padding: '10px', fontSize: '11px', border: '2px solid #000', background: '#FEFEE3' }}
                      />
                    </div>
                    
                    <div style={{ marginBottom: '20px' }}>
                      <label style={{ fontSize: '11px', color: '#4C956C', fontWeight: 'bold', display: 'block', marginBottom: '10px' }}>
                        SELECT PRODUCTS ({lookbookForm.product_ids.length} selected)
                      </label>
                      <div style={{ maxHeight: '300px', overflowY: 'auto', border: '2px solid #000', padding: '10px', background: '#FEFEE3' }}>
                        {products.map((product) => (
                          <div 
                            key={product._id}
                            onClick={() => toggleProductInLookbook(product._id)}
                            style={{ 
                              padding: '8px',
                              marginBottom: '5px',
                              background: lookbookForm.product_ids.includes(product._id) ? '#4AE54A' : '#FFF',
                              border: '2px solid #000',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px'
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={lookbookForm.product_ids.includes(product._id)}
                              readOnly
                              style={{ width: '16px', height: '16px' }}
                            />
                            <span style={{ fontSize: '11px', fontWeight: lookbookForm.product_ids.includes(product._id) ? 'bold' : 'normal' }}>
                              {product.name} - ${product.base_price}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button
                        onClick={handleSaveLookbook}
                        className="retro-btn"
                        style={{
                          background: '#4AE54A',
                          color: '#0a0a0a',
                          padding: '12px 24px',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          border: '3px solid #000',
                          flex: 1
                        }}
                        data-testid="save-lookbook-btn"
                      >
                        ✓ SAVE COLLECTION
                      </button>
                      <button
                        onClick={() => setShowLookbookForm(false)}
                        className="retro-btn"
                        style={{
                          background: '#FFF',
                          color: '#2C2C2C',
                          padding: '12px 24px',
                          fontSize: '12px',
                          border: '2px solid #000'
                        }}
                      >
                        ✗ CANCEL
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {activeTab === 'sim' && (
              <SimManager />
            )}

            {activeTab === 'players' && (
              <div data-testid="players-tab">
                <div className="retro-card" style={{ background: '#FFF', padding: '20px', marginBottom: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                    <div>
                      <h3 style={{ fontSize: '14px', color: '#2C6E49', marginBottom: '4px' }}>PLAYER SELECTION LEADERBOARD</h3>
                      <p style={{ fontSize: '11px', color: '#4C956C', margin: 0 }}>
                        Total selections: <strong style={{ color: '#2C2C2C' }}>{playerStatsTotal}</strong>
                      </p>
                    </div>
                    <button
                      onClick={loadPlayerStats}
                      disabled={playerStatsLoading}
                      data-testid="players-refresh-btn"
                      style={{
                        background: '#2C6E49',
                        color: '#FEFEE3',
                        padding: '8px 14px',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        border: '3px solid #000',
                        boxShadow: '3px 3px 0 0 #000',
                        cursor: playerStatsLoading ? 'not-allowed' : 'pointer',
                        letterSpacing: '1.5px'
                      }}
                    >
                      {playerStatsLoading ? '⏳' : '🔄 REFRESH'}
                    </button>
                  </div>
                </div>

                {playerStatsLoading ? (
                  <p style={{ textAlign: 'center', color: '#4C956C' }}>LOADING STATS...</p>
                ) : playerStats.length === 0 ? (
                  <div className="retro-card" style={{ background: '#FFF', padding: '30px', textAlign: 'center' }}>
                    <p style={{ fontSize: '36px', margin: '0 0 10px 0' }}>🎮</p>
                    <p style={{ color: '#4C956C', fontSize: '12px' }}>NO PLAYER SELECTIONS YET</p>
                    <p style={{ color: '#999', fontSize: '10px', marginTop: '6px' }}>
                      Selections are recorded when users click START GAME after choosing an avatar.
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Top 3 podium */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginBottom: '18px' }}>
                      {playerStats.slice(0, 3).map((p, i) => {
                        const medal = ['🥇', '🥈', '🥉'][i];
                        const bg = ['#FFD700', '#C0C0C0', '#CD7F32'][i];
                        return (
                          <div key={p.player_name} className="retro-card" style={{
                            background: '#FFF',
                            border: `4px solid ${bg}`,
                            padding: '18px',
                            textAlign: 'center',
                            boxShadow: '4px 4px 0 0 #000'
                          }}>
                            <div style={{ fontSize: '28px', marginBottom: '6px' }}>{medal}</div>
                            <div style={{ fontSize: '44px', margin: '4px 0', lineHeight: 1 }}>{p.emoji || '🎮'}</div>
                            <p style={{ fontSize: '13px', color: '#2C6E49', fontWeight: 'bold', letterSpacing: '2px', margin: '6px 0 3px 0', textTransform: 'uppercase' }}>
                              {p.player_name}
                            </p>
                            <p style={{ fontSize: '22px', color: '#2C2C2C', fontWeight: 'bold', margin: '6px 0 2px 0' }}>
                              {p.count}
                            </p>
                            <p style={{ fontSize: '9px', color: '#999', letterSpacing: '1.5px', margin: 0 }}>
                              SELECTIONS
                            </p>
                          </div>
                        );
                      })}
                    </div>

                    {/* Full ranked list */}
                    <div className="retro-card" style={{ background: '#FFF', padding: '0', overflow: 'hidden' }}>
                      {playerStats.map((p, idx) => {
                        const pct = playerStatsTotal > 0 ? (p.count / playerStatsTotal) * 100 : 0;
                        return (
                          <div key={p.player_name} data-testid={`player-row-${p.player_name.toLowerCase()}`} style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '12px 16px',
                            borderBottom: idx < playerStats.length - 1 ? '1px solid #E0E0E0' : 'none',
                            gap: '14px'
                          }}>
                            <div style={{ fontSize: '12px', color: '#999', fontWeight: 'bold', width: '30px' }}>
                              #{idx + 1}
                            </div>
                            <div style={{ fontSize: '32px', lineHeight: 1, width: '42px', textAlign: 'center' }}>
                              {p.emoji || '🎮'}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <p style={{ margin: '0 0 4px 0', fontSize: '13px', color: '#2C2C2C', fontWeight: 'bold', letterSpacing: '1px', textTransform: 'uppercase' }}>
                                {p.player_name}
                              </p>
                              <div style={{ background: '#F0F0F0', height: '8px', border: '1px solid #000', position: 'relative', overflow: 'hidden' }}>
                                <div style={{ background: '#4AE54A', height: '100%', width: `${pct}%`, transition: 'width 300ms ease' }} />
                              </div>
                              <p style={{ fontSize: '10px', color: '#4C956C', margin: '4px 0 0 0' }}>
                                {p.unique_users} unique user{p.unique_users === 1 ? '' : 's'}
                                {p.last_selected_at && ` · last: ${new Date(p.last_selected_at).toLocaleDateString()}`}
                              </p>
                            </div>
                            <div style={{ textAlign: 'right', minWidth: '72px' }}>
                              <p style={{ fontSize: '20px', color: '#2C2C2C', fontWeight: 'bold', margin: '0 0 2px 0' }}>
                                {p.count}
                              </p>
                              <p style={{ fontSize: '10px', color: '#888', margin: 0 }}>
                                {pct.toFixed(0)}%
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            )}
            
            {activeTab === 'newsletter' && (
              <div>
                {/* ============ CUSTOM EMAIL COMPOSER ============ */}
                <div
                  className="retro-card"
                  data-testid="custom-email-composer"
                  style={{
                    background: '#0F0F0F',
                    padding: '20px',
                    marginBottom: '25px',
                    border: '4px solid #4AE54A',
                    boxShadow: '4px 4px 0 0 #000',
                    color: '#FEFEE3',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px', flexWrap: 'wrap', gap: '8px' }}>
                    <h2 style={{ fontSize: '16px', color: '#FFC857', letterSpacing: '2px', margin: 0 }}>
                      ✉ COMPOSE CUSTOM EMAIL
                    </h2>
                    <button
                      onClick={loadCustomAudience}
                      data-testid="refresh-audience-count"
                      style={{
                        background: '#1E4B32', color: '#4AE54A',
                        padding: '6px 10px', fontSize: '10px', border: '2px solid #4AE54A',
                        cursor: 'pointer', letterSpacing: '0.1em', fontWeight: 'bold',
                      }}
                    >
                      ↻ AUDIENCE
                    </button>
                  </div>
                  <p style={{ fontSize: '11px', color: '#CCCCCC', margin: '4px 0 14px 0', lineHeight: 1.5 }}>
                    Send a one-off email to every customer on file — newsletter subscribers, giveaway
                    entries, past shoppers, and account holders (deduplicated automatically). A &quot;SHOP
                    THE LOOP&quot; button linking to <span style={{ color: '#4AE54A' }}>/shop</span> is
                    appended to every email.
                  </p>

                  {customAudience && (
                    <div
                      data-testid="audience-summary"
                      style={{
                        background: '#1E4B32', color: '#FEFEE3', padding: '10px 12px',
                        marginBottom: '14px', border: '2px solid #4AE54A', fontSize: '11px',
                        letterSpacing: '0.08em', display: 'flex', justifyContent: 'space-between',
                        flexWrap: 'wrap', gap: '8px',
                      }}
                    >
                      <span>
                        📬 <strong style={{ color: '#FFC857' }}>{customAudience.sendable}</strong> SENDABLE
                        <span style={{ color: '#7A8A82', marginLeft: '10px' }}>
                          ({customAudience.total_customers} unique • {customAudience.skipped_unsubscribed} opted out)
                        </span>
                      </span>
                    </div>
                  )}

                  <label style={{ fontSize: '10px', color: '#4AE54A', letterSpacing: '0.15em', display: 'block', marginBottom: '4px' }}>
                    SUBJECT LINE
                  </label>
                  <input
                    type="text"
                    value={customSubject}
                    onChange={(e) => setCustomSubject(e.target.value)}
                    placeholder="e.g. NEW DROP ALERT — CHECK OUT THE JULY GEAR"
                    data-testid="custom-subject-input"
                    style={{
                      width: '100%', minWidth: 0, padding: '10px 12px', marginBottom: '14px',
                      background: '#FEFEE3', color: '#000', border: '3px solid #4AE54A',
                      fontFamily: 'IBM Plex Mono, monospace', fontSize: '13px',
                      letterSpacing: '0.08em', textTransform: 'uppercase',
                    }}
                  />

                  <label style={{ fontSize: '10px', color: '#4AE54A', letterSpacing: '0.15em', display: 'block', marginBottom: '4px' }}>
                    BODY (HTML SUPPORTED)
                  </label>
                  <textarea
                    value={customBody}
                    onChange={(e) => setCustomBody(e.target.value)}
                    rows={9}
                    placeholder={"e.g.\n\n<p>Big drop hit the shop this week — pixel-print polos and the new Amen Corner hats.</p>\n\n<p>Members get first pick. Use code <strong>LOOPER10</strong> for 10% off your first order.</p>"}
                    data-testid="custom-body-textarea"
                    style={{
                      width: '100%', minWidth: 0, padding: '12px', marginBottom: '6px',
                      background: '#FEFEE3', color: '#000', border: '3px solid #4AE54A',
                      fontFamily: 'IBM Plex Mono, monospace', fontSize: '12px',
                      lineHeight: 1.6, resize: 'vertical',
                    }}
                  />
                  <p style={{ fontSize: '10px', color: '#7A8A82', margin: '0 0 14px 0', lineHeight: 1.5 }}>
                    Basic HTML supported: <code>&lt;p&gt;</code>, <code>&lt;strong&gt;</code>, <code>&lt;a href=&quot;…&quot;&gt;</code>,
                    <code>&lt;img src=&quot;…&quot;&gt;</code>, <code>&lt;ul&gt;/&lt;li&gt;</code>, <code>&lt;br&gt;</code>.
                    The retro shell + greeting + &quot;SHOP THE LOOP&quot; button are added automatically.
                  </p>

                  <div style={{ display: 'flex', gap: '10px', alignItems: 'stretch', flexWrap: 'wrap', marginBottom: '10px' }}>
                    <input
                      type="email"
                      value={customTestEmail}
                      onChange={(e) => setCustomTestEmail(e.target.value)}
                      placeholder="test@yourdomain.com"
                      data-testid="custom-test-email-input"
                      style={{
                        flex: '1 1 200px', minWidth: 0, padding: '10px 12px',
                        background: '#FEFEE3', color: '#000', border: '3px solid #FFC857',
                        fontFamily: 'IBM Plex Mono, monospace', fontSize: '12px',
                      }}
                    />
                    <button
                      onClick={handleCustomPreview}
                      disabled={customSending}
                      data-testid="custom-preview-btn"
                      style={{
                        background: '#000', color: '#4AE54A', padding: '10px 14px',
                        border: '3px solid #4AE54A', fontSize: '11px', letterSpacing: '0.1em',
                        fontWeight: 'bold', cursor: customSending ? 'not-allowed' : 'pointer',
                        opacity: customSending ? 0.6 : 1,
                      }}
                    >
                      👁 PREVIEW
                    </button>
                    <button
                      onClick={handleCustomTestSend}
                      disabled={customSending}
                      data-testid="custom-test-send-btn"
                      style={{
                        background: '#FFC857', color: '#000', padding: '10px 14px',
                        border: '3px solid #000', fontSize: '11px', letterSpacing: '0.1em',
                        fontWeight: 'bold', cursor: customSending ? 'not-allowed' : 'pointer',
                        opacity: customSending ? 0.6 : 1,
                      }}
                    >
                      📤 SEND TEST
                    </button>
                  </div>

                  <button
                    onClick={handleCustomBlast}
                    disabled={customSending}
                    data-testid="custom-blast-btn"
                    style={{
                      width: '100%', background: '#4AE54A', color: '#000', padding: '14px',
                      border: '3px solid #000', boxShadow: '3px 3px 0 0 #000',
                      fontSize: '13px', letterSpacing: '0.15em', fontWeight: 'bold',
                      cursor: customSending ? 'not-allowed' : 'pointer',
                      opacity: customSending ? 0.6 : 1,
                    }}
                  >
                    {customSending ? '⏳ SENDING…' : '🚀 SEND TO ALL CUSTOMERS'}
                  </button>

                  {/* Live progress panel (shared shape with template blaster) */}
                  {blastProgress && blastProgress._label === 'CUSTOM EMAIL' && (
                    <div
                      data-testid="custom-blast-progress"
                      style={{
                        marginTop: '18px', padding: '14px', background: '#000',
                        border: '2px solid #FFC857', color: '#FEFEE3',
                      }}
                    >
                      <p style={{ fontSize: '11px', color: '#FFC857', letterSpacing: '0.12em', marginBottom: '10px' }}>
                        BLAST STATUS — {blastProgress.status.toUpperCase()}
                      </p>
                      <div style={{ background: '#1E4B32', height: '18px', border: '2px solid #4AE54A', marginBottom: '8px', position: 'relative' }}>
                        <div
                          style={{
                            background: '#4AE54A', height: '100%',
                            width: blastProgress.total_queued > 0
                              ? `${Math.round(((blastProgress.sent + blastProgress.failed) / blastProgress.total_queued) * 100)}%`
                              : '0%',
                            transition: 'width 300ms ease',
                          }}
                        />
                      </div>
                      <p style={{ fontSize: '11px', color: '#CCCCCC' }}>
                        📤 {blastProgress.sent} sent • ❌ {blastProgress.failed} failed •
                        📬 {blastProgress.total_queued} queued • 🚫 {blastProgress.skipped_unsubscribed} opted out
                      </p>
                      {blastProgress.failed_emails && blastProgress.failed_emails.length > 0 && (
                        <details style={{ marginTop: '8px', fontSize: '10px', color: '#FF9B9B' }}>
                          <summary style={{ cursor: 'pointer' }}>
                            {blastProgress.failed_emails.length} FAILED — CLICK TO VIEW
                          </summary>
                          <pre style={{ whiteSpace: 'pre-wrap', marginTop: '6px', color: '#FF9B9B' }}>
                            {blastProgress.failed_emails.join('\n')}
                          </pre>
                        </details>
                      )}
                    </div>
                  )}
                </div>
                {/* ============ /CUSTOM EMAIL COMPOSER ============ */}

                {!Array.isArray(newsletterSubscribers) || newsletterSubscribers.length === 0 ? (
                  <p style={{ textAlign: 'center', color: '#4C956C' }}>NO NEWSLETTER SUBSCRIBERS YET</p>
                ) : (
                  newsletterSubscribers.map((subscriber) => (
                    <div key={subscriber._id} className="retro-card" style={{ background: '#FFF', padding: '15px', marginBottom: '10px', display: 'flex', gap: '15px', alignItems: 'center' }}>
                      <div style={{ width: '50px', height: '50px', border: '2px solid #000', background: '#4AE54A', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span style={{ fontSize: '24px' }}>📧</span>
                      </div>
                      
                      <div style={{ flex: 1 }}>
                        <h3 style={{ fontSize: '14px', color: '#2C2C2C', fontWeight: 'bold', marginBottom: '3px' }}>{subscriber.full_name}</h3>
                        <p style={{ fontSize: '11px', color: '#4C956C' }}>{subscriber.email}</p>
                        <p style={{ fontSize: '10px', color: '#999', marginTop: '3px' }}>
                          Subscribed: {new Date(subscriber.subscribed_at).toLocaleDateString()}
                        </p>
                      </div>
                      
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <button 
                          onClick={async () => {
                            if (window.confirm('Delete this subscriber?')) {
                              await api.adminDeleteNewsletterSubscriber(subscriber._id);
                              await loadData();
                            }
                          }}
                          className="retro-btn" 
                          style={{ background: '#FFF', border: '2px solid #FF5A5F', padding: '8px 12px', fontSize: '11px' }}
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
            
            {activeTab === 'giveaway' && (
              <div>
                <div style={{ background: '#2C6E49', color: '#FEFEE3', padding: '20px', marginBottom: '20px', border: '3px solid #000', boxShadow: '4px 4px 0 0 #000' }}>
                  <h2 style={{ fontSize: '18px', marginBottom: '10px', fontWeight: 'bold' }}>$100 GIVEAWAY TRACKER</h2>
                  <p style={{ fontSize: '12px', marginBottom: '5px' }}>
                    <strong>{giveawayEntries.length}/50</strong> entries recorded
                  </p>
                  <p style={{ fontSize: '12px', color: '#FFC857' }}>
                    {giveawayEntries.length >= 50 ? '🎉 GIVEAWAY IS FULL!' : `${50 - giveawayEntries.length} spots remaining`}
                  </p>
                  
                  <div style={{ display: 'flex', gap: '10px', marginTop: '15px', flexWrap: 'wrap' }}>
                    <button
                      onClick={async () => {
                        if (giveawayEntries.length === 0) {
                          alert('No giveaway entries to spin yet!');
                          return;
                        }
                        // Refresh winners history before opening
                        try {
                          const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/giveaway/winners?limit=20`);
                          const data = await res.json();
                          setRecentWinners(Array.isArray(data.winners) ? data.winners : []);
                        } catch (e) { /* noop */ }
                        setShowSpinWheel(true);
                      }}
                      className="retro-btn"
                      style={{
                        background: '#FFC857',
                        color: '#0a0a0a',
                        padding: '10px 20px',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        border: '3px solid #000',
                        boxShadow: '3px 3px 0 0 #000'
                      }}
                      data-testid="open-spin-wheel-btn"
                    >
                      🎡 SPIN THE WHEEL
                    </button>

                    <button
                      onClick={handleSyncNewsletterToGiveaway}
                      className="retro-btn"
                      style={{
                        background: '#4AE54A',
                        color: '#0a0a0a',
                        padding: '10px 20px',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        border: '3px solid #000',
                        boxShadow: '3px 3px 0 0 #000'
                      }}
                      data-testid="sync-newsletter-btn"
                    >
                      📧 SYNC NEWSLETTER
                    </button>
                    
                    <button
                      onClick={() => {
                        if (giveawayEntries.length === 0) {
                          alert('No giveaway entries to email yet!');
                          return;
                        }
                        const emailSection = document.getElementById('giveaway-email-section');
                        if (emailSection) {
                          emailSection.scrollIntoView({ behavior: 'smooth' });
                          emailSection.style.animation = 'pulse 0.5s';
                        }
                      }}
                      className="retro-btn"
                      style={{
                        background: '#FFC857',
                        color: '#0a0a0a',
                        padding: '10px 20px',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        border: '3px solid #000',
                        boxShadow: '3px 3px 0 0 #000'
                      }}
                    >
                      ✉️ EMAIL ALL PARTICIPANTS
                    </button>
                  </div>
                  
                  <p style={{ fontSize: '10px', marginTop: '8px', color: '#FFC857' }}>
                    Sync adds newsletter subscribers to giveaway (skips duplicates)
                  </p>
                </div>

                {/* TEMPLATE EMAIL QUICK-SEND BUTTONS */}
                <div
                  style={{
                    background: '#0F0F0F',
                    color: '#4AE54A',
                    padding: '20px',
                    marginBottom: '20px',
                    border: '3px solid #4AE54A',
                    boxShadow: '4px 4px 0 0 #000',
                    fontFamily: "'Courier New', monospace"
                  }}
                  data-testid="template-email-section"
                >
                  <h3 style={{
                    color: '#FFC857',
                    fontSize: '15px',
                    letterSpacing: '2px',
                    marginBottom: '6px',
                    fontWeight: 'bold',
                    textTransform: 'uppercase'
                  }}>
                    🎮 PRE-BUILT TEMPLATE BLASTS
                  </h3>
                  <p style={{ fontSize: '11px', color: '#AAAAAA', marginBottom: '18px', lineHeight: 1.6 }}>
                    Preview, send a test, or blast any template to all {giveawayEntries.length} participants. Unsubscribed users are skipped automatically.
                  </p>
                  <div style={{ display: 'grid', gap: '12px', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
                    <button
                      onClick={() => openTemplatePreview('welcome')}
                      data-testid="template-welcome-btn"
                      style={{
                        background: '#4AE54A',
                        color: '#000',
                        padding: '16px 14px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        letterSpacing: '1.5px',
                        border: '3px solid #000',
                        boxShadow: '4px 4px 0 0 #000',
                        cursor: 'pointer',
                        textTransform: 'uppercase',
                        fontFamily: "'Courier New', monospace",
                        minHeight: '60px'
                      }}
                    >
                      ⛳ WELCOME TO THE LOOP
                      <div style={{ fontSize: '9px', marginTop: '4px', color: '#0a3a0a', fontWeight: 'normal', letterSpacing: '1px' }}>
                        Greeting + giveaway confirm
                      </div>
                    </button>
                    <button
                      onClick={() => openTemplatePreview('newsletter')}
                      data-testid="template-newsletter-btn"
                      style={{
                        background: '#FFC857',
                        color: '#000',
                        padding: '16px 14px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        letterSpacing: '1.5px',
                        border: '3px solid #000',
                        boxShadow: '4px 4px 0 0 #000',
                        cursor: 'pointer',
                        textTransform: 'uppercase',
                        fontFamily: "'Courier New', monospace",
                        minHeight: '60px'
                      }}
                    >
                      📰 NEWSLETTER
                      <div style={{ fontSize: '9px', marginTop: '4px', color: '#4a3a00', fontWeight: 'normal', letterSpacing: '1px' }}>
                        New drops &amp; monthly dispatch
                      </div>
                    </button>
                    <button
                      onClick={() => openTemplatePreview('giveaway')}
                      data-testid="template-giveaway-btn"
                      style={{
                        background: '#E85A5A',
                        color: '#000',
                        padding: '16px 14px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        letterSpacing: '1.5px',
                        border: '3px solid #000',
                        boxShadow: '4px 4px 0 0 #000',
                        cursor: 'pointer',
                        textTransform: 'uppercase',
                        fontFamily: "'Courier New', monospace",
                        minHeight: '60px'
                      }}
                    >
                      🎁 GIVEAWAY UPDATE
                      <div style={{ fontSize: '9px', marginTop: '4px', color: '#4a0a0a', fontWeight: 'normal', letterSpacing: '1px' }}>
                        Entry # + status update
                      </div>
                    </button>
                  </div>
                </div>
                
                {/* Email All Participants Section */}
                {giveawayEntries.length > 0 && (
                  <div 
                    id="giveaway-email-section"
                    style={{ 
                      background: '#FFF', 
                      padding: '20px', 
                      marginBottom: '20px', 
                      border: '3px solid #FFC857', 
                      boxShadow: '4px 4px 0 0 #000' 
                    }}
                  >
                    <h3 style={{ fontSize: '16px', color: '#2C6E49', marginBottom: '15px', fontWeight: 'bold' }}>
                      📧 SEND CUSTOM EMAIL TO ALL {giveawayEntries.length} PARTICIPANTS
                    </h3>
                    
                    <div style={{ marginBottom: '15px' }}>
                      <label style={{ fontSize: '11px', color: '#4C956C', marginBottom: '5px', display: 'block', fontWeight: 'bold' }}>
                        EMAIL SUBJECT:
                      </label>
                      <input
                        type="text"
                        id="giveaway-email-subject"
                        placeholder="e.g., 🎉 Giveaway Update from The Loop Golf!"
                        style={{
                          width: '100%',
                          padding: '10px',
                          fontSize: '13px',
                          fontFamily: 'IBM Plex Mono, monospace',
                          border: '3px solid #000',
                          boxShadow: '3px 3px 0 0 #000',
                          background: '#FEFEE3'
                        }}
                      />
                    </div>
                    
                    <div style={{ marginBottom: '15px' }}>
                      <label style={{ fontSize: '11px', color: '#4C956C', marginBottom: '5px', display: 'block', fontWeight: 'bold' }}>
                        EMAIL MESSAGE:
                      </label>
                      <p style={{ fontSize: '10px', color: '#999', marginBottom: '8px' }}>
                        Use {'{name}'} for their name, {'{entry_number}'} for their entry number
                      </p>
                      <textarea
                        id="giveaway-email-message"
                        rows={8}
                        placeholder={`Example:\n\nHey {name}!\n\nWe wanted to give you an update on the $100 Loop Golf Giveaway!\n\nYour entry number is #{entry_number} and you're officially in the running.\n\nThe winner will be announced on [DATE]. Good luck! 🍀\n\nP.S. Check out our latest drops at theloopgolf.com`}
                        style={{
                          width: '100%',
                          padding: '12px',
                          fontSize: '13px',
                          fontFamily: 'IBM Plex Mono, monospace',
                          border: '3px solid #000',
                          boxShadow: '3px 3px 0 0 #000',
                          background: '#FEFEE3',
                          resize: 'vertical'
                        }}
                      />
                    </div>
                    
                    <button
                      onClick={async () => {
                        const subject = document.getElementById('giveaway-email-subject').value;
                        const message = document.getElementById('giveaway-email-message').value;
                        
                        if (!subject || !message) {
                          alert('⚠️ Please fill in both subject and message!');
                          return;
                        }
                        
                        if (!window.confirm(`Send email to all ${giveawayEntries.length} giveaway participants?\n\nSubject: ${subject}\n\nThis action cannot be undone.`)) {
                          return;
                        }
                        
                        try {
                          const button = event.target;
                          button.disabled = true;
                          button.textContent = 'SENDING...';
                          
                          const result = await api.adminSendGiveawayEmail(subject, message, true);
                          
                          if (result.success) {
                            alert(`✅ EMAIL SENT!\n\nSent: ${result.sent}\nFailed: ${result.failed}\nTotal: ${result.total_entries}`);
                            
                            // Clear the form
                            document.getElementById('giveaway-email-subject').value = '';
                            document.getElementById('giveaway-email-message').value = '';
                          } else {
                            alert(`⚠️ ${result.message}`);
                          }
                          
                          button.disabled = false;
                          button.textContent = '📤 SEND EMAIL TO ALL';
                          
                        } catch (error) {
                          console.error('Error sending emails:', error);
                          alert('❌ Failed to send emails. Check console for details.');
                          event.target.disabled = false;
                          event.target.textContent = '📤 SEND EMAIL TO ALL';
                        }
                      }}
                      className="retro-btn"
                      style={{
                        background: '#2C6E49',
                        color: '#FEFEE3',
                        padding: '12px 24px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        border: '3px solid #000',
                        boxShadow: '4px 4px 0 0 #000',
                        cursor: 'pointer'
                      }}
                    >
                      📤 SEND EMAIL TO ALL
                    </button>
                    
                    <p style={{ fontSize: '10px', color: '#999', marginTop: '10px' }}>
                      ⚠️ This will send immediately to all {giveawayEntries.length} participants. Double-check your message!
                    </p>
                  </div>
                )}

                {giveawayEntries.length === 0 ? (
                  <p style={{ textAlign: 'center', color: '#4C956C' }}>NO GIVEAWAY ENTRIES YET</p>
                ) : (
                  giveawayEntries.map((entry) => (
                    <div 
                      key={entry._id} 
                      className="retro-card" 
                      style={{ 
                        background: '#FFF', 
                        padding: '15px', 
                        marginBottom: '10px', 
                        display: 'flex', 
                        gap: '15px', 
                        alignItems: 'center',
                        border: entry.entry_number <= 50 ? '3px solid #4AE54A' : '2px solid #DDD'
                      }}
                    >
                      <div style={{ 
                        width: '60px', 
                        height: '60px', 
                        border: '2px solid #000', 
                        background: entry.entry_number <= 50 ? '#FFC857' : '#CCC',
                        flexShrink: 0, 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        fontWeight: 'bold',
                        fontSize: '20px'
                      }}>
                        #{entry.entry_number}
                      </div>
                      
                      <div style={{ flex: 1 }}>
                        <h3 style={{ fontSize: '14px', color: '#2C2C2C', fontWeight: 'bold', marginBottom: '3px' }}>
                          {entry.name}
                        </h3>
                        <p style={{ fontSize: '11px', color: '#4C956C', marginBottom: '2px' }}>{entry.email}</p>
                        <p style={{ fontSize: '10px', color: '#999' }}>
                          Entered: {new Date(entry.entered_at).toLocaleString()}
                          {entry.source && ` · Source: ${entry.source}`}
                        </p>
                      </div>
                      
                      <div style={{ textAlign: 'right' }}>
                        <span style={{ 
                          fontSize: '10px', 
                          padding: '4px 8px', 
                          background: entry.status === 'winner' ? '#4AE54A' : entry.status === 'contacted' ? '#FFC857' : '#CCC',
                          color: '#000',
                          fontWeight: 'bold',
                          borderRadius: '3px',
                          display: 'inline-block',
                          marginBottom: '5px'
                        }}>
                          {entry.status.toUpperCase()}
                        </span>
                      </div>
                      
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <select
                          value={entry.status}
                          onChange={async (e) => {
                            await api.adminUpdateGiveawayStatus(entry._id, e.target.value);
                            await loadData();
                          }}
                          style={{
                            padding: '6px',
                            fontSize: '11px',
                            border: '2px solid #000'
                          }}
                        >
                          <option value="entered">Entered</option>
                          <option value="contacted">Contacted</option>
                          <option value="winner">Winner</option>
                          <option value="disqualified">Disqualified</option>
                        </select>
                        <button 
                          onClick={async () => {
                            if (window.confirm('Delete this entry?')) {
                              await api.adminDeleteGiveawayEntry(entry._id);
                              await loadData();
                            }
                          }}
                          className="retro-btn" 
                          style={{ background: '#FFF', border: '2px solid #FF5A5F', padding: '8px 12px', fontSize: '11px' }}
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
            
            {activeTab === 'members' && (
              <div>
                {/* Sync Members banner */}
                <div className="retro-card" style={{ background: '#FFF', padding: '16px 20px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                  <div style={{ flex: 1, minWidth: '220px' }}>
                    <p style={{ fontSize: '12px', color: '#2C6E49', margin: '0 0 4px 0', fontWeight: 'bold', letterSpacing: '1.5px' }}>
                      📇 MEMBERS SYNC
                    </p>
                    <p style={{ fontSize: '10px', color: '#4C956C', margin: 0, lineHeight: 1.5 }}>
                      Pulls every newsletter subscriber, giveaway entry, and order customer into the Members tab. Safe to re-run anytime — idempotent.
                    </p>
                  </div>
                  <button
                    onClick={async () => {
                      const email = window.prompt('Customer email (e.g. brycebrandt@example.com):');
                      if (!email || !email.trim()) return;
                      const amountStr = window.prompt('Credit amount in dollars (positive=add, negative=deduct):', '100');
                      if (amountStr === null) return;
                      const amount = parseFloat(amountStr);
                      if (Number.isNaN(amount) || amount === 0) {
                        alert('Invalid amount');
                        return;
                      }
                      const reason = window.prompt('Reason:', 'Giveaway winner: $100 store credit');
                      if (!reason || !reason.trim()) return;
                      try {
                        const r = await api.adminAdjustCustomerCredit(email.trim(), amount, reason.trim());
                        alert(`✓ CREDIT APPLIED\n\nEmail: ${email.trim()}\nNew Balance: $${Number(r.balance).toFixed(2)}\n\nThe credit will be auto-applied at the customer's next checkout.`);
                      } catch (e) {
                        alert('✗ Failed: ' + (e.message || 'Unknown'));
                      }
                    }}
                    data-testid="grant-credit-by-email-btn"
                    style={{
                      background: '#4AE54A',
                      color: '#0a0a0a',
                      padding: '10px 18px',
                      fontSize: '11px',
                      fontWeight: 'bold',
                      border: '3px solid #000',
                      boxShadow: '3px 3px 0 0 #000',
                      cursor: 'pointer',
                      letterSpacing: '1.5px',
                      fontFamily: "'Courier New', monospace",
                      whiteSpace: 'nowrap',
                      marginRight: '10px'
                    }}
                  >
                    💰 GRANT CREDIT BY EMAIL
                  </button>
                  <button
                    onClick={async () => {
                      if (!window.confirm('Backfill Members tab from all newsletter subscribers, giveaway entries, and order customers?')) return;
                      try {
                        const r = await api.adminBackfillMembers();
                        alert(`✓ SYNC COMPLETE\n\nCreated: ${r.created}\nAlready existed: ${r.already_existed}\nScanned: ${r.scanned_emails}\nTotal members now: ${r.total_user_profiles}`);
                        loadData();
                      } catch (e) {
                        alert('✗ Sync failed: ' + (e.message || 'Unknown'));
                      }
                    }}
                    data-testid="backfill-members-btn"
                    style={{
                      background: '#FFC857',
                      color: '#0a0a0a',
                      padding: '10px 18px',
                      fontSize: '11px',
                      fontWeight: 'bold',
                      border: '3px solid #000',
                      boxShadow: '3px 3px 0 0 #000',
                      cursor: 'pointer',
                      letterSpacing: '1.5px',
                      fontFamily: "'Courier New', monospace",
                      whiteSpace: 'nowrap'
                    }}
                  >
                    🔄 SYNC MEMBERS NOW
                  </button>
                </div>

                {!Array.isArray(customers) || customers.length === 0 ? (
                  <p style={{ textAlign: 'center', color: '#4C956C' }}>NO CUSTOMER PROFILES YET</p>
                ) : (
                  <>
                    {customers.map((customer) => (
                      <div 
                        key={customer._id} 
                        className="retro-card" 
                        onClick={async () => {
                          try {
                            const details = await api.adminGetCustomerDetails(customer.email);
                            if (!details || details.detail) {
                              alert(`Could not load member: ${details?.detail || 'unknown error'}`);
                              return;
                            }
                            setSelectedCustomer({ ...customer, ...details, email: details.email || customer.email });
                          } catch (err) {
                            console.error('Member load failed:', err);
                            alert('Failed to load member profile: ' + (err?.message || 'Unknown error'));
                          }
                        }}
                        style={{ 
                          background: '#FFF', 
                          padding: '15px', 
                          marginBottom: '10px', 
                          display: 'flex', 
                          gap: '15px', 
                          alignItems: 'center',
                          cursor: 'pointer',
                          transition: 'transform 0.2s'
                        }}
                      >
                        <div style={{ width: '60px', height: '60px', border: '3px solid #000', background: '#2C6E49', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '3px 3px 0 0 #000' }}>
                          <span style={{ fontSize: '28px', color: '#FEFEE3' }}>👤</span>
                        </div>
                        
                        <div style={{ flex: 1 }}>
                          <h3 style={{ fontSize: '14px', color: '#2C2C2C', fontWeight: 'bold', marginBottom: '3px' }}>{customer.full_name || customer.name || 'N/A'}</h3>
                          <p style={{ fontSize: '11px', color: '#4C956C', marginBottom: '2px' }}>{customer.email}</p>
                          <p style={{ fontSize: '10px', color: '#999' }}>
                            {Number(customer.total_orders) || 0} orders · ${(Number(customer.total_spent) || 0).toFixed(2)} total
                          </p>
                        </div>
                        
                        <div style={{ textAlign: 'right' }}>
                          <p style={{ fontSize: '10px', color: '#4C956C', marginBottom: '3px' }}>MEMBER SINCE</p>
                          <p style={{ fontSize: '11px', color: '#2C2C2C', fontWeight: 'bold' }}>
                            {new Date(customer.created_at || customer.first_order_date).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    ))}
                    
                    {/* Customer Detail Modal */}
                    {selectedCustomer && (
                      <div 
                        style={{ 
                          position: 'fixed', 
                          inset: 0, 
                          background: 'rgba(0,0,0,0.8)', 
                          display: 'flex', 
                          justifyContent: 'center', 
                          alignItems: 'center', 
                          zIndex: 1000,
                          padding: '20px'
                        }}
                        onClick={() => setSelectedCustomer(null)}
                      >
                        <div 
                          style={{ 
                            background: '#FEFEE3', 
                            maxWidth: '800px', 
                            width: '100%',
                            maxHeight: '90vh',
                            overflowY: 'auto',
                            border: '4px solid #000',
                            boxShadow: '8px 8px 0 0 #000',
                            padding: '0'
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {/* Header */}
                          <div style={{ background: '#2C6E49', padding: '20px', borderBottom: '4px solid #000' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <h2 style={{ fontSize: '18px', color: '#FEFEE3', letterSpacing: '0.12em' }}>[ CUSTOMER PROFILE ]</h2>
                              <button
                                onClick={() => setSelectedCustomer(null)}
                                style={{ 
                                  background: '#FFF', 
                                  border: '3px solid #000', 
                                  color: '#2C2C2C', 
                                  padding: '8px 16px', 
                                  fontSize: '12px',
                                  cursor: 'pointer',
                                  boxShadow: '3px 3px 0 0 #000'
                                }}
                                className="retro-btn"
                              >
                                ✕ CLOSE
                              </button>
                            </div>
                          </div>
                          
                          {/* Content */}
                          <div style={{ padding: '20px' }}>
                            {/* Customer Info */}
                            <div style={{ background: '#FFF', padding: '20px', marginBottom: '20px', border: '3px solid #000', boxShadow: '4px 4px 0 0 #000' }}>
                              <h3 style={{ fontSize: '14px', color: '#2C6E49', marginBottom: '15px', letterSpacing: '0.12em' }}>[ INFO ]</h3>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px' }}>
                                <div>
                                  <p style={{ fontSize: '10px', color: '#4C956C', marginBottom: '5px' }}>NAME</p>
                                  <p style={{ fontSize: '13px', color: '#2C2C2C', fontWeight: 'bold' }}>{selectedCustomer.name || 'N/A'}</p>
                                </div>
                                <div>
                                  <p style={{ fontSize: '10px', color: '#4C956C', marginBottom: '5px' }}>EMAIL</p>
                                  <p style={{ fontSize: '13px', color: '#2C2C2C' }}>{selectedCustomer.email}</p>
                                </div>
                                <div>
                                  <p style={{ fontSize: '10px', color: '#4C956C', marginBottom: '5px' }}>PHONE</p>
                                  <p style={{ fontSize: '13px', color: '#2C2C2C' }}>{selectedCustomer.phone || 'N/A'}</p>
                                </div>
                                <div>
                                  <p style={{ fontSize: '10px', color: '#4C956C', marginBottom: '5px' }}>STATUS</p>
                                  <span style={{ background: '#2C6E49', color: '#FEFEE3', padding: '4px 12px', fontSize: '10px', fontWeight: 'bold', display: 'inline-block' }}>
                                    {selectedCustomer.status?.toUpperCase() || 'ACTIVE'}
                                  </span>
                                </div>
                              </div>
                            </div>
                            
                            {/* Order Stats */}
                            <div style={{ background: '#FFF', padding: '20px', marginBottom: '20px', border: '3px solid #000', boxShadow: '4px 4px 0 0 #000' }}>
                              <h3 style={{ fontSize: '14px', color: '#2C6E49', marginBottom: '15px', letterSpacing: '0.12em' }}>[ STATS ]</h3>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px' }}>
                                <div>
                                  <p style={{ fontSize: '10px', color: '#4C956C', marginBottom: '5px' }}>TOTAL ORDERS</p>
                                  <p style={{ fontSize: '20px', color: '#2C2C2C', fontWeight: 'bold' }}>{Number(selectedCustomer.total_orders) || 0}</p>
                                </div>
                                <div>
                                  <p style={{ fontSize: '10px', color: '#4C956C', marginBottom: '5px' }}>TOTAL SPENT</p>
                                  <p style={{ fontSize: '20px', color: '#2C2C2C', fontWeight: 'bold' }}>${(Number(selectedCustomer.total_spent) || 0).toFixed(2)}</p>
                                </div>
                                <div>
                                  <p style={{ fontSize: '10px', color: '#4C956C', marginBottom: '5px' }}>AVG ORDER</p>
                                  <p style={{ fontSize: '20px', color: '#2C2C2C', fontWeight: 'bold' }}>
                                    ${(() => {
                                      const o = Number(selectedCustomer.total_orders) || 0;
                                      const s = Number(selectedCustomer.total_spent) || 0;
                                      return (o > 0 ? s / o : 0).toFixed(2);
                                    })()}
                                  </p>
                                </div>
                              </div>
                            </div>

                            {/* Store Credit */}
                            {selectedCustomer.email && (
                              <CustomerCreditPanel email={selectedCustomer.email} />
                            )}
                            
                            {/* Shipping Addresses */}
                            {selectedCustomer.shipping_addresses && selectedCustomer.shipping_addresses.length > 0 && (
                              <div style={{ background: '#FFF', padding: '20px', marginBottom: '20px', border: '3px solid #000', boxShadow: '4px 4px 0 0 #000' }}>
                                <h3 style={{ fontSize: '14px', color: '#2C6E49', marginBottom: '15px', letterSpacing: '0.12em' }}>[ ADDRESSES ]</h3>
                                {selectedCustomer.shipping_addresses.map((addr, idx) => (
                                  <div key={`${addr.address || ''}-${addr.zip_code || ''}-${idx}`} style={{ marginBottom: '10px', paddingBottom: '10px', borderBottom: idx < selectedCustomer.shipping_addresses.length - 1 ? '2px solid #F7F6D8' : 'none' }}>
                                    <p style={{ fontSize: '12px', color: '#2C2C2C' }}>{addr.address}</p>
                                    <p style={{ fontSize: '11px', color: '#4C956C' }}>{addr.city}, {addr.state} {addr.zip_code}</p>
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            {/* Order History */}
                            <div style={{ background: '#FFF', padding: '20px', border: '3px solid #000', boxShadow: '4px 4px 0 0 #000' }}>
                              <h3 style={{ fontSize: '14px', color: '#2C6E49', marginBottom: '15px', letterSpacing: '0.12em' }}>[ ORDER HISTORY ]</h3>
                              {selectedCustomer.orders && selectedCustomer.orders.length > 0 ? (
                                selectedCustomer.orders.map((order) => (
                                  <div key={order._id} style={{ marginBottom: '10px', paddingBottom: '10px', borderBottom: '2px solid #F7F6D8' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                      <div>
                                        <p style={{ fontSize: '11px', color: '#2C2C2C', fontWeight: 'bold' }}>#{order._id.slice(-8)}</p>
                                        <p style={{ fontSize: '10px', color: '#999' }}>
                                          {new Date(order.created_at).toLocaleDateString()} · {order.items?.length || 0} items
                                        </p>
                                      </div>
                                      <div style={{ textAlign: 'right' }}>
                                        <p style={{ fontSize: '13px', color: '#2C2C2C', fontWeight: 'bold' }}>${order.total.toFixed(2)}</p>
                                        <span style={{ 
                                          background: order.payment_status === 'paid' ? '#2C6E49' : '#FFC857',
                                          color: '#FFF',
                                          padding: '2px 8px',
                                          fontSize: '9px',
                                          fontWeight: 'bold'
                                        }}>
                                          {order.payment_status?.toUpperCase() || 'PENDING'}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <p style={{ fontSize: '11px', color: '#999', textAlign: 'center' }}>No orders found</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>


      {/* RETRY PAYMENT MODAL */}
      {retryPaymentOrder && (
        <div
          data-testid="retry-payment-modal"
          onClick={closeRetryPaymentModal}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.88)',
            zIndex: 2500,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            overflowY: 'auto',
            fontFamily: "'Courier New', monospace"
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#0F0F0F',
              border: '4px solid #FFC857',
              boxShadow: '0 0 30px rgba(255,200,87,0.4)',
              maxWidth: '640px',
              width: '100%',
              maxHeight: '92vh',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {/* Header */}
            <div style={{
              background: 'linear-gradient(180deg,#1a1a1a 0%,#0F0F0F 100%)',
              padding: '18px 22px',
              borderBottom: '3px solid #FFC857',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '10px'
            }}>
              <div>
                <h2 style={{ color: '#FFC857', fontSize: '17px', letterSpacing: '2px', margin: 0, textTransform: 'uppercase' }}>
                  💳 RETRY PAYMENT
                </h2>
                <p style={{ color: '#4AE54A', fontSize: '10px', letterSpacing: '1.5px', margin: '4px 0 0 0' }}>
                  ORDER #{retryPaymentOrder._id.slice(-8).toUpperCase()} · ${(retryPaymentOrder.total || 0).toFixed(2)}
                </p>
              </div>
              <button
                onClick={closeRetryPaymentModal}
                data-testid="retry-payment-close"
                style={{
                  background: 'transparent',
                  color: '#E85A5A',
                  border: '2px solid #E85A5A',
                  padding: '6px 14px',
                  fontSize: '11px',
                  fontWeight: 'bold',
                  cursor: (retryPaymentLoading || retryPaymentSendingEmail) ? 'not-allowed' : 'pointer',
                  letterSpacing: '2px',
                  fontFamily: "'Courier New', monospace"
                }}
              >
                ✕ CLOSE
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: '20px 22px', overflowY: 'auto', flex: 1 }}>
              {retryPaymentLoading ? (
                <p style={{ textAlign: 'center', color: '#FFC857', fontSize: '13px', letterSpacing: '2px', padding: '30px 0' }}>
                  ⏳ GENERATING SECURE PAYMENT LINK...
                </p>
              ) : (
                <>
                  <div style={{ background: 'rgba(74,229,74,0.08)', border: '2px solid #2C6E49', padding: '14px 16px', marginBottom: '18px' }}>
                    <p style={{ color: '#4AE54A', fontSize: '11px', margin: '0 0 8px 0', letterSpacing: '1.5px', fontWeight: 'bold' }}>
                      CUSTOMER ON FILE:
                    </p>
                    <p style={{ color: '#DDDDDD', fontSize: '13px', margin: 0, wordBreak: 'break-all' }}>
                      {retryPaymentOrder.email || retryPaymentOrder.customer_email || <span style={{ color: '#E85A5A' }}>(no email on file)</span>}
                    </p>
                  </div>

                  <label style={{ display: 'block', color: '#FFC857', fontSize: '11px', letterSpacing: '2px', fontWeight: 'bold', marginBottom: '6px' }}>
                    🔗 SECURE STRIPE PAYMENT LINK:
                  </label>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
                    <input
                      type="text"
                      readOnly
                      value={retryPaymentLink}
                      data-testid="retry-payment-link-input"
                      onClick={(e) => e.target.select()}
                      style={{
                        flex: '1 1 240px',
                        padding: '10px 12px',
                        background: '#000',
                        color: '#4AE54A',
                        border: '2px solid #4AE54A',
                        fontSize: '11px',
                        fontFamily: "'Courier New', monospace",
                        boxSizing: 'border-box'
                      }}
                    />
                    <button
                      onClick={handleCopyRetryLink}
                      data-testid="retry-payment-copy-btn"
                      style={{
                        background: retryPaymentCopied ? '#4AE54A' : '#2C6E49',
                        color: retryPaymentCopied ? '#000' : '#FEFEE3',
                        padding: '10px 16px',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        border: '3px solid #000',
                        boxShadow: '3px 3px 0 0 #000',
                        cursor: 'pointer',
                        letterSpacing: '1.5px',
                        fontFamily: "'Courier New', monospace"
                      }}
                    >
                      {retryPaymentCopied ? '✓ COPIED!' : '📋 COPY LINK'}
                    </button>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '10px', marginBottom: '20px' }}>
                    <a
                      href={retryPaymentLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="retry-payment-open-btn"
                      style={{
                        display: 'block',
                        background: '#FFC857',
                        color: '#0a0a0a',
                        padding: '12px 14px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        border: '3px solid #000',
                        boxShadow: '4px 4px 0 0 #000',
                        cursor: 'pointer',
                        textAlign: 'center',
                        textDecoration: 'none',
                        letterSpacing: '1.5px',
                        textTransform: 'uppercase',
                        fontFamily: "'Courier New', monospace"
                      }}
                    >
                      🌐 OPEN LINK (charge myself)
                    </a>
                  </div>

                  <div style={{ paddingTop: '16px', borderTop: '2px dashed #2C6E49' }}>
                    <label style={{ display: 'block', color: '#FFC857', fontSize: '11px', letterSpacing: '2px', fontWeight: 'bold', marginBottom: '6px' }}>
                      📧 EMAIL TO CUSTOMER (optional note):
                    </label>
                    <textarea
                      value={retryPaymentEmailMessage}
                      onChange={(e) => setRetryPaymentEmailMessage(e.target.value)}
                      rows={3}
                      placeholder="Optional: Hey! Thanks for chatting earlier - here's the link to complete your order..."
                      data-testid="retry-payment-message-input"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        background: '#000',
                        color: '#DDDDDD',
                        border: '2px solid #2C6E49',
                        fontSize: '13px',
                        fontFamily: "'Courier New', monospace",
                        marginBottom: '10px',
                        boxSizing: 'border-box',
                        resize: 'vertical'
                      }}
                    />
                    <button
                      onClick={handleEmailRetryLink}
                      disabled={retryPaymentSendingEmail || !(retryPaymentOrder.email || retryPaymentOrder.customer_email)}
                      data-testid="retry-payment-email-btn"
                      style={{
                        width: '100%',
                        background: retryPaymentSendingEmail ? '#AAA' : '#4AE54A',
                        color: '#000',
                        padding: '14px 20px',
                        fontSize: '13px',
                        fontWeight: 'bold',
                        border: '3px solid #000',
                        boxShadow: '4px 4px 0 0 #000',
                        cursor: retryPaymentSendingEmail ? 'not-allowed' : 'pointer',
                        letterSpacing: '2px',
                        textTransform: 'uppercase',
                        opacity: (retryPaymentOrder.email || retryPaymentOrder.customer_email) ? 1 : 0.4,
                        fontFamily: "'Courier New', monospace"
                      }}
                    >
                      {retryPaymentSendingEmail
                        ? '⏳ SENDING...'
                        : `📨 EMAIL SECURE LINK TO CUSTOMER`}
                    </button>
                  </div>

                  <div style={{ marginTop: '16px', padding: '12px 14px', background: '#1a1a1a', border: '1px solid #444' }}>
                    <p style={{ color: '#888', fontSize: '10px', letterSpacing: '1px', margin: 0, lineHeight: 1.6 }}>
                      <strong style={{ color: '#FFC857' }}>ℹ TIP:</strong> Once the customer (or you on their behalf) completes payment via this link, hit <strong>🔄 SYNC PAYMENTS</strong> on the Orders tab to reconcile the order status locally. It will also happen automatically when they land on the success page.
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}


      {/* CUSTOM EMAIL PREVIEW MODAL */}
      {customPreviewHtml !== null && (
        <div
          data-testid="custom-preview-modal"
          onClick={() => setCustomPreviewHtml(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)', zIndex: 2100,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
            overflowY: 'auto',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#FEFEE3', maxWidth: '720px', width: '100%',
              border: '4px solid #4AE54A', boxShadow: '6px 6px 0 0 #000',
              maxHeight: '90vh', display: 'flex', flexDirection: 'column',
            }}
          >
            <div style={{ padding: '14px 18px', background: '#0F0F0F', color: '#FFC857', borderBottom: '3px solid #4AE54A', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '12px', letterSpacing: '0.15em', fontWeight: 'bold' }}>
                👁 CUSTOM EMAIL PREVIEW — {customSubject}
              </span>
              <button
                onClick={() => setCustomPreviewHtml(null)}
                data-testid="close-custom-preview"
                style={{
                  background: '#FF5A5F', color: '#FFF', padding: '6px 10px',
                  border: '2px solid #000', fontSize: '11px', fontWeight: 'bold',
                  cursor: 'pointer', letterSpacing: '0.1em',
                }}
              >
                ✕ CLOSE
              </button>
            </div>
            <iframe
              title="Custom Email Preview"
              srcDoc={customPreviewHtml}
              style={{ flex: 1, width: '100%', minHeight: '480px', border: 'none', background: '#000' }}
            />
          </div>
        </div>
      )}


      {/* TEMPLATE EMAIL PREVIEW MODAL */}
      {templateModal && (
        <div
          data-testid="template-preview-modal"
          onClick={closeTemplateModal}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.88)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            overflowY: 'auto',
            fontFamily: "'Courier New', monospace"
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#0F0F0F',
              border: '4px solid #4AE54A',
              boxShadow: '0 0 30px rgba(74,229,74,0.4)',
              maxWidth: '820px',
              width: '100%',
              maxHeight: '92vh',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {/* Modal Header */}
            <div style={{
              background: 'linear-gradient(180deg,#1a1a1a 0%,#0F0F0F 100%)',
              padding: '18px 22px',
              borderBottom: '3px solid #4AE54A',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '10px'
            }}>
              <div>
                <h2 style={{ color: '#FFC857', fontSize: '18px', letterSpacing: '2px', margin: 0, textTransform: 'uppercase' }}>
                  📬 {templateModal.label}
                </h2>
                <p style={{ color: '#888', fontSize: '10px', letterSpacing: '1.5px', margin: '4px 0 0 0' }}>
                  [ TEMPLATE PREVIEW ]
                </p>
              </div>
              <button
                onClick={closeTemplateModal}
                data-testid="template-modal-close"
                style={{
                  background: 'transparent',
                  color: '#E85A5A',
                  border: '2px solid #E85A5A',
                  padding: '6px 14px',
                  fontSize: '11px',
                  fontWeight: 'bold',
                  cursor: templateSending ? 'not-allowed' : 'pointer',
                  letterSpacing: '2px',
                  fontFamily: "'Courier New', monospace"
                }}
              >
                ✕ CLOSE
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '18px 22px', overflowY: 'auto', flex: 1 }}>
              {/* Subject editor */}
              <label style={{ display: 'block', color: '#4AE54A', fontSize: '11px', letterSpacing: '2px', fontWeight: 'bold', marginBottom: '6px' }}>
                SUBJECT LINE:
              </label>
              <input
                type="text"
                value={templateSubject}
                onChange={(e) => setTemplateSubject(e.target.value)}
                data-testid="template-subject-input"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  background: '#000',
                  color: '#FFC857',
                  border: '2px solid #4AE54A',
                  fontSize: '13px',
                  fontFamily: "'Courier New', monospace",
                  marginBottom: '16px',
                  boxSizing: 'border-box'
                }}
              />

              {/* Test Email Row */}
              <label style={{ display: 'block', color: '#4AE54A', fontSize: '11px', letterSpacing: '2px', fontWeight: 'bold', marginBottom: '6px' }}>
                SEND TEST TO:
              </label>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
                <input
                  type="email"
                  value={templateTestEmail}
                  onChange={(e) => setTemplateTestEmail(e.target.value)}
                  placeholder="your@email.com"
                  data-testid="template-test-email-input"
                  style={{
                    flex: '1 1 220px',
                    padding: '10px 12px',
                    background: '#000',
                    color: '#CCCCCC',
                    border: '2px solid #2C6E49',
                    fontSize: '13px',
                    fontFamily: "'Courier New', monospace",
                    boxSizing: 'border-box'
                  }}
                />
                <button
                  onClick={handleTemplateSendTest}
                  disabled={templateSending}
                  data-testid="template-send-test-btn"
                  style={{
                    background: '#2C6E49',
                    color: '#FEFEE3',
                    padding: '10px 18px',
                    fontSize: '11px',
                    fontWeight: 'bold',
                    border: '3px solid #000',
                    boxShadow: '3px 3px 0 0 #000',
                    cursor: templateSending ? 'not-allowed' : 'pointer',
                    letterSpacing: '1.5px',
                    opacity: templateSending ? 0.6 : 1,
                    fontFamily: "'Courier New', monospace"
                  }}
                >
                  {templateSending ? '⏳ SENDING...' : '📨 SEND TEST'}
                </button>
              </div>

              {/* HTML Preview */}
              <label style={{ display: 'block', color: '#4AE54A', fontSize: '11px', letterSpacing: '2px', fontWeight: 'bold', marginBottom: '6px' }}>
                PREVIEW:
              </label>
              <div style={{
                border: '2px solid #2C6E49',
                background: '#FFFFFF',
                maxHeight: '420px',
                overflowY: 'auto'
              }}>
                <iframe
                  title="Email Preview"
                  srcDoc={templateModal.html}
                  data-testid="template-preview-iframe"
                  style={{ width: '100%', height: '420px', border: 'none', background: '#fff' }}
                />
              </div>

              {/* Live Blast Progress */}
              {blastProgress && (
                <div
                  data-testid="blast-progress"
                  style={{
                    marginTop: '18px',
                    padding: '16px 18px',
                    border: `3px solid ${blastProgress.status === 'completed' ? '#4AE54A' : blastProgress.status === 'failed' ? '#E85A5A' : '#FFC857'}`,
                    background: '#000',
                    fontFamily: "'Courier New', monospace"
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap', gap: '8px' }}>
                    <span style={{ color: '#FFC857', fontSize: '12px', letterSpacing: '2px', fontWeight: 'bold' }}>
                      {blastProgress.status === 'completed' ? '✓ BLAST COMPLETE' :
                       blastProgress.status === 'failed' ? '✗ BLAST FAILED' :
                       blastProgress.status === 'running' ? '⚡ BLASTING...' : '⏳ QUEUED'}
                    </span>
                    <span style={{ color: '#AAAAAA', fontSize: '10px', letterSpacing: '1px' }}>
                      ID: {blastProgress.blast_id?.slice(0, 8) || '—'}
                    </span>
                  </div>
                  {/* Progress bar */}
                  <div style={{ background: '#1a1a1a', height: '18px', border: '2px solid #2C6E49', position: 'relative', overflow: 'hidden' }}>
                    <div
                      style={{
                        background: blastProgress.status === 'failed' ? '#E85A5A' : '#4AE54A',
                        height: '100%',
                        width: blastProgress.total_queued
                          ? `${Math.min(100, ((blastProgress.sent + blastProgress.failed) / blastProgress.total_queued) * 100)}%`
                          : '0%',
                        transition: 'width 300ms ease'
                      }}
                    />
                    <div style={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '10px',
                      color: '#000',
                      fontWeight: 'bold',
                      letterSpacing: '1px',
                      textShadow: '0 0 4px rgba(255,255,255,0.5)'
                    }}>
                      {blastProgress.sent + blastProgress.failed} / {blastProgress.total_queued}
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: '8px', marginTop: '12px', fontSize: '11px', letterSpacing: '1px' }}>
                    <div style={{ color: '#4AE54A' }}>
                      ✅ SENT: <strong>{blastProgress.sent}</strong>
                    </div>
                    <div style={{ color: '#E85A5A' }}>
                      ❌ FAILED: <strong>{blastProgress.failed}</strong>
                    </div>
                    <div style={{ color: '#AAAAAA' }}>
                      ⏭ SKIPPED: <strong>{blastProgress.skipped_unsubscribed}</strong>
                    </div>
                    <div style={{ color: '#FFC857' }}>
                      📋 QUEUED: <strong>{blastProgress.total_queued}</strong>
                    </div>
                  </div>
                  {blastProgress.failed_emails && blastProgress.failed_emails.length > 0 && (
                    <div style={{ marginTop: '10px', padding: '8px 10px', background: '#2a0a0a', border: '1px solid #E85A5A', maxHeight: '80px', overflowY: 'auto' }}>
                      <p style={{ color: '#E85A5A', fontSize: '10px', margin: '0 0 4px 0', letterSpacing: '1px' }}>FAILED RECIPIENTS:</p>
                      {blastProgress.failed_emails.map((em, i) => (
                        <div key={`${em}-${i}`} style={{ color: '#CCCCCC', fontSize: '10px', wordBreak: 'break-all' }}>• {em}</div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Modal Footer: Blast CTA */}
            <div style={{
              padding: '16px 22px',
              borderTop: '3px solid #4AE54A',
              background: '#0a0a0a',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '10px'
            }}>
              <p style={{ color: '#888', fontSize: '10px', letterSpacing: '1px', margin: 0, flex: 1, minWidth: '200px' }}>
                ⚠️ BLAST SENDS TO ALL {giveawayEntries.length} PARTICIPANTS<br />
                <span style={{ color: '#4AE54A' }}>Runs in background · unsubscribed users skipped · live progress above.</span>
              </p>
              <button
                onClick={handleTemplateSendBlast}
                disabled={templateSending || giveawayEntries.length === 0 || blastProgress?.status === 'completed'}
                data-testid="template-send-blast-btn"
                style={{
                  background: blastProgress?.status === 'completed' ? '#2C6E49' : '#E85A5A',
                  color: blastProgress?.status === 'completed' ? '#FEFEE3' : '#000',
                  padding: '14px 28px',
                  fontSize: '13px',
                  fontWeight: 'bold',
                  border: '3px solid #000',
                  boxShadow: '4px 4px 0 0 #000',
                  cursor: templateSending || giveawayEntries.length === 0 || blastProgress?.status === 'completed' ? 'not-allowed' : 'pointer',
                  letterSpacing: '2px',
                  textTransform: 'uppercase',
                  opacity: templateSending || giveawayEntries.length === 0 ? 0.7 : 1,
                  fontFamily: "'Courier New', monospace"
                }}
              >
                {blastProgress?.status === 'completed'
                  ? '✓ COMPLETE'
                  : templateSending
                    ? '⏳ BLASTING...'
                    : `🚀 SEND TO ALL ${giveawayEntries.length}`}
              </button>
            </div>
          </div>
        </div>
      )}
      {showSpinWheel && (
        <SpinWheel
          entries={giveawayEntries}
          onClose={() => setShowSpinWheel(false)}
          onWinner={async (w) => {
            // Refresh entries (winner is now flagged) + winner history
            try {
              const [entriesRes, winnersRes] = await Promise.allSettled([
                api.adminGetGiveawayEntries(),
                fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/giveaway/winners?limit=20`).then(r => r.json()),
              ]);
              if (entriesRes.status === 'fulfilled') setGiveawayEntries(entriesRes.value || []);
              if (winnersRes.status === 'fulfilled') setRecentWinners(Array.isArray(winnersRes.value?.winners) ? winnersRes.value.winners : []);
            } catch (e) { /* noop */ }
          }}
        />
      )}
    </div>
  );
};

export default Admin;
